import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import TimeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from "@fullcalendar/interaction";
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import moment from "moment";
import { Modal, Form } from "react-bootstrap";
import { useUserAPI } from '../../contexts/UserAPIContext';
import { useNotification } from '../../contexts/NotificationContext';
import CustomAutoComplete from "../../components/CustomAutoComplete";
import { authFetch } from '../../utils/authFetch';
import DateInput from '../../components/DateInput';

const Calendario = React.memo(() => {
    const { userAPIData } = useUserAPI();
    const { showNotification } = useNotification();
    const calendarRef = useRef(null);
    const isInitialized = useRef(false);
    const lastFetchKey = useRef(null);
    const eventosCache = useRef(new Map()); // Cache dos eventos por período
    const currentView = useRef('timeGridWeek'); // Controla a visualização atual
    const isFetching = useRef(false); // Controla se já está fazendo uma busca
    const lastDatesSet = useRef(null); // Controla o último datesSet processado

    // Verificar se é role 6 (somente leitura)
    const isReadOnly = userAPIData?.userRoleId === 6;

    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentRange, setCurrentRange] = useState(null);

    // Estados para filtros
    const [terapeutasFiltro, setTerapeutasFiltro] = useState([]);
    const [pacientesFiltro, setPacientesFiltro] = useState([]);
    const [terapeutasSelecionadasFiltro, setTerapeutasSelecionadasFiltro] = useState([]);
    const [pacientesSelecionadosFiltro, setPacientesSelecionadosFiltro] = useState([]);
    const [tiposSelecionadosFiltro, setTiposSelecionadosFiltro] = useState([]);
    const [pacientesDesselecionadosFiltro, setPacientesDesselecionadosFiltro] = useState([]);
    const [terapeutasDesselecionadosFiltro, setTerapeutasDesselecionadosFiltro] = useState([]);
    const [tiposDesselecionadosFiltro, setTiposDesselecionadosFiltro] = useState([]);

    // Estados para controle de "selecionar todos" dos filtros
    const [todosTerapSelecionados, setTodosTerapSelecionados] = useState(true);
    const [todosPacientesSelecionados, setTodosPacientesSelecionados] = useState(true);
    const [todosTiposSelecionados, setTodosTiposSelecionados] = useState(true);

    // Estado para busca de pacientes
    const [buscaPacienteFiltro, setBuscaPacienteFiltro] = useState('');
    const [pacientesFiltroFiltrados, setPacientesFiltroFiltrados] = useState([]);

    // Estado para busca de terapeutas
    const [buscaTerapeutaFiltro, setBuscaTerapeutaFiltro] = useState('');
    const [terapeutasFiltroFiltradas, setTerapeutasFiltroFiltradas] = useState([]);

    // Estado para controlar expansão dos filtros
    const [filtrosExpandidos, setFiltrosExpandidos] = useState(false);

    // Estados para modais de replicar
    const [showModalReplicarSem, setShowModalReplicarSem] = useState(false);
    const [showModalReplicarDia, setShowModalReplicarDia] = useState(false);

    // Estados para replicação
    const [dataInicioRepli, setDataInicioRepli] = useState("");
    const [dataFimRepli, setDataFimRepli] = useState("");
    const [semOrigem, setSemOrigem] = useState("");
    const [semDestino, setSemDestino] = useState("");

    // States para o modal de novo evento
    const [showModalAdd, setShowModalAdd] = useState(false);
    const [titulo, setTitulo] = useState("");
    const [dataInicio, setDataInicio] = useState("");
    const [dataFim, setDataFim] = useState("");
    const [horaInicio, setHoraInicio] = useState("08:00");
    const [horaFim, setHoraFim] = useState("09:00");
    const [allDay, setAllDay] = useState(false);
    const [tipoAgendAdd, setTipoAgendAdd] = useState(1);
    const [reposicao, setReposicao] = useState(0);
    const [consideraValor, setConsideraValor] = useState(false);

    // States para paciente
    const [pacienteSearch, setPacienteSearch] = useState('');
    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
    const [planosPaciente, setPlanosPaciente] = useState([]);

    // States para terapeuta
    const [terapeutaSearch, setTerapeutaSearch] = useState('');
    const [terapeutaSelecionado, setTerapeutaSelecionado] = useState(null);
    const [funcoesTerapeuta, setFuncoesTerapeuta] = useState([]);
    const [outrasTerapeutas, setOutrasTerapeutas] = useState([]);
    const [outraTerapSearch, setOutraTerapSearch] = useState('');
    const [idsTerapeutasExtras, setIdsTerapeutasExtras] = useState([]);
    const [agendamentosRelacionados, setAgendamentosRelacionados] = useState([]);

    // Form data
    const [formAddEvent, setFormAddEvent] = useState({
        startDatetime: "",
        endDateTime: "",
        note: "",
        tipo: 1,
        title: "",
        patientId: "",
        planId: "",
        therapistId: "",
        jobPositionId: "",
        roomId: "",
        value: "",
    });

    // Refs
    const planosRef = useRef(null);

    // States para o modal de edição
    const [showModalEdit, setShowModalEdit] = useState(false);
    const [idEventoSelecionado, setIdEventoSelecionado] = useState(null);
    const [tipoAgendEdit, setTipoAgendEdit] = useState(0);
    const [falta, setFalta] = useState(0);
    const [faturada, setFaturada] = useState(0);
    const [infoFalta, setInfoFalta] = useState(null);
    const [infoCriacao, setInfoCriacao] = useState(null);
    const [geraReposicao, setGeraReposicao] = useState(0);
    const [pacienteFoiSelecionadoExternamente, setPacienteFoiSelecionadoExternamente] = useState(false);
    const [terapeutaFoiSelecionadoExternamente, setTerapeutaFoiSelecionadoExternamente] = useState(false);

    // Form data para edição
    const [formEditEvent, setFormEditEvent] = useState({
        id: "",
        startDatetime: "",
        endDateTime: "",
        note: "",
        tipo: 1,
        title: "",
        patientId: "",
        planId: "",
        therapistId: "",
        jobPositionId: "",
        roomId: "",
        value: "",
        therapists: []
    });

    // Estados para desabilitar campos conforme regras
    const [disabledFalta, setDisabledFalta] = useState(false);
    const [disabledFaturada, setDisabledFaturada] = useState(false);
    const [disabledGeraRepo, setDisabledGeraRepo] = useState(false);

    // Estados para modal de confirmação de exclusão
    const [showModalConfirmacao, setShowModalConfirmacao] = useState(false);
    const [eventoParaExcluir, setEventoParaExcluir] = useState(null);

    // Tipos de Agendamento
    const tiposAgendamento = [
        { id: 0, nome: "Livre" },
        { id: 1, nome: "Sessão" },
        { id: 2, nome: "Avulso" }
    ];

    // Gerar horários
    const gerarHorarios = () => {
        const horarios = [];
        for (let h = 0; h < 24; h++) {
            for (let m of [0, 30]) {
                const hora = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                horarios.push(hora);
            }
        }
        return horarios;
    };
    const horarios = gerarHorarios();

    // Função para formatar data para a API (YYYY-MM-DD)
    const formatDateForAPI = (date) => {
        // Garantir que estamos trabalhando com uma data válida
        const dateObj = new Date(date);

        // Usar o timezone local para evitar problemas de offset
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');

        const formatted = `${year}-${month}-${day}`;

        return formatted;
    };

    // Função para obter mês e ano de uma data
    const getMonthYear = (date) => {
        const momentDate = moment(date);
        return {
            month: momentDate.format('MM'),
            year: momentDate.format('YYYY')
        };
    };

    // Função para obter o mês e ano atual da view do calendário
    const getCurrentMonthYear = useCallback(() => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            const currentDate = calendarApi.getDate();
            return getMonthYear(currentDate);
        }
        return null;
    }, []);

    // Funções para o modal de novo evento
    const dataAtual = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const arredondarHoraAtual = () => {
        const now = new Date();
        const minutes = now.getMinutes();
        const roundedMinutes = minutes < 30 ? 0 : 30;
        now.setMinutes(roundedMinutes, 0, 0);
        return now.toTimeString().slice(0, 5);
    };

    const horaFinalPadrao = () => {
        const now = new Date();
        const minutes = now.getMinutes();
        const roundedMinutes = minutes < 30 ? 0 : 30;
        now.setMinutes(roundedMinutes, 0, 0);
        now.setHours(now.getHours() + 1);
        return now.toTimeString().slice(0, 5);
    };

    const alteraTipo = (e) => {
        const novoTipo = Number(e.target.value);
        const tipoAnterior = tipoAgendAdd;
        
        setTipoAgendAdd(novoTipo);

        // Se o tipo anterior era 1 (sessao) e agora mudou para outro tipo, limpar o planId
        if (tipoAnterior === 1 && novoTipo !== 1) {
            setFormAddEvent(prev => ({ ...prev, planId: "" }));
        }

        if (novoTipo === 1 && pacienteSelecionado?.id) {
            buscarPlanos();
        } else {
            setPlanosPaciente([]);
        }
    };

    const alteraTipoEdit = (e) => {
        const novoTipo = Number(e.target.value);
        const tipoAnterior = tipoAgendEdit;
        
        setTipoAgendEdit(novoTipo);

        // Se o tipo anterior era 1 (sessao) e agora mudou para outro tipo, limpar o planId
        if (tipoAnterior === 1 && novoTipo !== 1) {
            setFormEditEvent(prev => ({ ...prev, planId: "" }));
        }

        if (novoTipo === 1 && pacienteSelecionado?.id) {
            buscarPlanosEdicao(pacienteSelecionado.id);
        } else {
            setPlanosPaciente([]);
        }
    };

    const alterReposicao = (e) => {
        setReposicao(e.target.checked ? 1 : 0);
    };

    const alterConsideraValor = (e) => {
        setConsideraValor(e.target.checked ? 1 : 0);
    };

    const handleDataInicioChange = (e) => {
        setDataInicio(e.target.value);
        setDataFim(e.target.value);
    };

    const handleDataFimChange = (e) => {
        setDataFim(e.target.value);
    };

    const handleHoraInicioChange = (e) => {
        const novaHoraInicio = e.target.value;
        setHoraInicio(novaHoraInicio);

        // Automaticamente ajustar hora fim para 1 hora depois
        const [hora, minuto] = novaHoraInicio.split(':');
        const novaHoraFim = `${String(parseInt(hora) + 1).padStart(2, '0')}:${minuto}`;
        setHoraFim(novaHoraFim);
    };

    const handleHoraFimChange = (e) => {
        setHoraFim(e.target.value);
    };

    const handleAllDayChange = (e) => {
        setAllDay(e.target.checked);
        if (e.target.checked) {
            setHoraInicio("00:00");
            setHoraFim("23:59");
        } else {
            setHoraInicio(arredondarHoraAtual());
            setHoraFim(horaFinalPadrao());
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormAddEvent(prev => ({ ...prev, [name]: value }));
    };

    // Buscar pacientes
    const buscarPacientes = async (termo) => {
        try {
            const response = await authFetch(`patient?search=${encodeURIComponent(termo)}`);
            if (!response.ok) throw new Error('Erro ao buscar pacientes');
            const data = await response.json();
            return data.filter(paciente =>
                paciente.name.toLowerCase().includes(termo.toLowerCase())
            );
        } catch (error) {
            console.error('Erro ao buscar pacientes:', error);
            return [];
        }
    };

    // Buscar planos do paciente
    const buscarPlanos = async () => {
        if (!pacienteSelecionado?.id) return;

        try {
            const response = await authFetch(`patient-plan/patient/${pacienteSelecionado.id}`);
            if (!response.ok) throw new Error('Erro ao buscar planos');
            const data = await response.json();
            setPlanosPaciente(data);

            // Auto-selecionar o primeiro plano se existir
            if (data.length > 0) {
                setFormAddEvent(prev => ({ ...prev, planId: data[0].plan.id }));
            }
        } catch (error) {
            console.error('Erro ao buscar planos:', error);
            setPlanosPaciente([]);
        }
    };

    // Buscar terapeutas
    const buscarTerapeutas = async (termo) => {
        try {
            const response = await authFetch(`therapist?search=${encodeURIComponent(termo)}&enabled=true`);
            if (!response.ok) throw new Error('Erro ao buscar terapeutas');
            const data = await response.json();
            return data.filter(terapeuta =>
                terapeuta.name.toLowerCase().includes(termo.toLowerCase())
            );
        } catch (error) {
            console.error('Erro ao buscar terapeutas:', error);
            return [];
        }
    };

    // Buscar funções da terapeuta
    const buscarFuncoes = async (terapeutaId) => {
        try {
            const response = await authFetch(`therapist-job-position/therapist/${terapeutaId}`);
            if (!response.ok) throw new Error('Erro ao buscar funções');
            const data = await response.json();
            setFuncoesTerapeuta(data);

            // Auto-selecionar a primeira função se existir
            if (data.length > 0) {
                setFormAddEvent(prev => ({ ...prev, jobPositionId: data[0].jobPositionId }));
            }
        } catch (error) {
            console.error('Erro ao buscar funções:', error);
            setFuncoesTerapeuta([]);
        }
    };

    // Buscar terapeutas extras
    const buscarTerapeutasExtras = async (termo) => {
        try {
            const response = await authFetch(`therapist?search=${encodeURIComponent(termo)}&enabled=true`);
            if (!response.ok) throw new Error('Erro ao buscar terapeutas');
            const data = await response.json();
            return data.filter(terapeuta =>
                terapeuta.name.toLowerCase().includes(termo.toLowerCase()) &&
                terapeuta.id !== terapeutaSelecionado?.id &&
                !idsTerapeutasExtras.includes(terapeuta.id)
            );
        } catch (error) {
            console.error('Erro ao buscar terapeutas extras:', error);
            return [];
        }
    };

    const handleSelecionarOutraTerapeuta = async (item) => {
        if (outrasTerapeutas.find((t) => t.id === item.id)) return;

        try {
            const response = await authFetch(`therapist-job-position/therapist/${item.id}`);

            if (!response.ok) {
                throw new Error('Erro ao buscar função da terapeuta');
            }

            const funcoesData = await response.json();
            const funcoes = funcoesData.map(f => ({
                id: f.jobPosition.id,
                name: f.jobPosition.name,
            }));

            const funcaoInicial = funcoes[0]?.id ?? null;

            const novaTerap = {
                ...item,
                funcoes,
                selectedFuncaoId: funcaoInicial,
                isNova: true,
            };

            setOutrasTerapeutas((prev) => [...prev, novaTerap]);
            setIdsTerapeutasExtras((prev) => [...prev, item.id]);
            setOutraTerapSearch('');

            setAgendamentosRelacionados((prev) => [
                ...prev,
                {
                    therapistId: item.id,
                    jobPositionId: funcaoInicial,
                }
            ]);
        } catch (error) {
            const novaTerap = {
                ...item,
                funcoes: [],
                selectedFuncaoId: null,
                isNova: true,
            };

            setOutrasTerapeutas((prev) => [...prev, novaTerap]);
            setIdsTerapeutasExtras((prev) => [...prev, item.id]);
            setOutraTerapSearch('');

            showNotification('danger', 'Função não encontrada para a terapeuta selecionada');
            console.error('Erro ao buscar funções da terapeuta extra:', error);
        }
    };

    const SetarTitulo = () => {        
        // Só setar título automaticamente se ele estiver vazio
        // if (!titulo && tipoAgendAdd === 1 && pacienteSelecionado && formAddEvent.planId) {
        //removi essa validação de setar titulo automaticamente somente se ele estiver vazio.
        //adicionei também essa validação se o modal de adicionar evento está aberto, para não alterar título quando entrar para visualização
        if (tipoAgendAdd === 1 && pacienteSelecionado && formAddEvent.planId && showModalAdd) {
            const planoSelecionado = planosPaciente.find(p => p.plan.id == formAddEvent.planId);
            if (planoSelecionado) {
                setTitulo(`${pacienteSelecionado.name} - ${planoSelecionado.plan.name}`);
            }
        }
    };

    // Função para setar título no modal de edição (quando usuário alterar o plano)
    const SetarTituloEdicao = () => {
        if (tipoAgendEdit === 1 && pacienteSelecionado && formEditEvent.planId && showModalEdit) {
            const planoSelecionado = planosPaciente.find(p => p.plan.id == formEditEvent.planId);
            if (planoSelecionado) {
                setTitulo(`${pacienteSelecionado.name} - ${planoSelecionado.plan.name}`);
            }
        }
    };

    // Effect para buscar planos quando paciente for selecionado
    useEffect(() => {
        if (pacienteSelecionado?.id && tipoAgendAdd === 1) {
            buscarPlanos();
        }
    }, [pacienteSelecionado, tipoAgendAdd]);

    // Effect para buscar funções quando terapeuta for selecionado
    useEffect(() => {
        if (terapeutaSelecionado?.id) {
            buscarFuncoes(terapeutaSelecionado.id);
        }
    }, [terapeutaSelecionado]);

    // Effect para setar título automaticamente no modal de adição
    useEffect(() => {
        SetarTitulo();
    }, [pacienteSelecionado, formAddEvent.planId, tipoAgendAdd, titulo]);



    // Effect para limpar título quando mudar tipo
    useEffect(() => {
        if (tipoAgendAdd !== 1) {
            setTitulo("");
        }
    }, [tipoAgendAdd]);

    // Função para verificar se pode agendar (validações)
    const podeAgendar = useMemo(() => {
        // Se for tipo sessão (1) e tem paciente selecionado, precisa ter plano
        if (tipoAgendAdd === 1 && pacienteSelecionado && (!formAddEvent.planId || planosPaciente.length === 0)) {
            return false;
        }

        // Se for tipo sessão (1) ou avulso (2) e tem terapeuta selecionada, precisa ter função
        if ((tipoAgendAdd === 1 || tipoAgendAdd === 2) && terapeutaSelecionado && (!formAddEvent.jobPositionId || funcoesTerapeuta.length === 0)) {
            return false;
        }

        return true;
    }, [tipoAgendAdd, pacienteSelecionado, formAddEvent.planId, planosPaciente.length, terapeutaSelecionado, formAddEvent.jobPositionId, funcoesTerapeuta.length]);

    // Função para adicionar novo evento
    const adicionarEvento = async (e) => {
        e.preventDefault();

        const dataInicioCompleta = createLocalDateTime(dataInicio, horaInicio);
        const dataFimCompleta = createLocalDateTime(dataFim, horaFim);

        let tipoFalta = 0;

        if (reposicao === 1) {
            tipoFalta = 3;
        } else {
            tipoFalta = 0;
        }

        // Payload base comum a todos os tipos
        const payload = {
            patientId: formAddEvent.patientId || null,
            planId: formAddEvent.planId || null,
            therapistId: formAddEvent.therapistId || null,
            jobPositionId: formAddEvent.jobPositionId || null,
            roomId: null,
            note: formAddEvent.note,
            startDatetime: formatDateTime(dataInicioCompleta),
            endDatetime: formatDateTime(dataFimCompleta),
            title: titulo,
            isAllDay: Boolean(allDay),
            color: terapeutaSelecionado?.color || "#eb6737",
            appointmentType: tipoAgendAdd,
            absenceType: tipoFalta,
            allowReschedule: reposicao === 1,
            chargeable: Boolean(consideraValor),
            therapists: agendamentosRelacionados.map(t => ({
                therapistId: Number(t.therapistId),
                jobPositionId: Number(t.jobPositionId),
            })),
        };

        // Ajustes específicos
        if (tipoAgendAdd === 0) {
            payload.patientId = null;
            payload.planId = null;
            payload.therapistId = null;
            payload.jobPositionId = null;
            payload.color = "#eb6737";
            payload.absenceType = 0;
            payload.chargeable = false;
            payload.allowReschedule = false;
            payload.therapists = [];
        }

        if (tipoAgendAdd === 2) {
            payload.color = "#000";
            payload.value = formAddEvent.value.replace(/\./g, "").replace(",", ".");
        }

        try {
            const res = await authFetch('appointment', {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error('Erro ao criar o evento');

            const novoEvento = await res.json();

            setShowModalAdd(false);

            // Limpar completamente o cache
            limparCache();

            // Forçar nova busca para o período atual
            await forcarAtualizacaoCalendario();

            showNotification('success', 'Evento criado com sucesso!');

        } catch (err) {
            console.error(err);
            showNotification('danger', 'Erro ao criar o evento');
        }
    };

    // Função para buscar eventos da API - suporta período, mês/ano e dia
    const buscarEventosAPI = useCallback(async (params, forceRefresh = false) => {
        let fetchKey, url;

        if (params.type === 'monthly') {
            // Busca mensal
            fetchKey = `month_${params.year}_${params.month}`;
            url = `appointment?month=${params.month}&year=${params.year}&master=true`;
        } else if (params.type === 'daily') {
            // Busca diária
            const dateFormatted = formatDateForAPI(params.date);
            fetchKey = `daily_${dateFormatted}`;
            url = `appointment?date=${dateFormatted}&master=true`;
        } else {
            // Busca por período (semanal, etc)
            const startFormatted = formatDateForAPI(params.startDate);
            const endFormatted = formatDateForAPI(params.endDate);
            fetchKey = `period_${startFormatted}_${endFormatted}`;
            url = `appointment?startDate=${startFormatted}&endDate=${endFormatted}&master=true`;
        }

        // Proteção contra múltiplas chamadas simultâneas
        if (isFetching.current) {
            return;
        }

        // Verifica se os dados já estão no cache (a menos que seja forçado refresh)
        if (!forceRefresh && eventosCache.current.has(fetchKey)) {
            const eventosCacheados = eventosCache.current.get(fetchKey);
            setEventos(eventosCacheados);
            lastFetchKey.current = fetchKey;
            return eventosCacheados;
        }

        // Evita buscar a mesma chave novamente se já está em processo
        if (lastFetchKey.current === fetchKey) {
            return;
        }

        isFetching.current = true;
        setLoading(true);
        lastFetchKey.current = fetchKey;

        try {
            const response = await authFetch(url);

            if (!response.ok) {
                throw new Error('Erro ao buscar eventos');
            }

            const data = await response.json();

            // Formatar eventos para o FullCalendar - usar datas direto da API
            const eventosFormatados = data.map((evento) => {

                // Função para converter data da API para horário local
                const parseLocalDate = (dateTimeString) => {
                    if (!dateTimeString) return null;

                    // Parse manual para garantir horário local
                    const dateStr = dateTimeString.replace('T', ' ').replace('Z', '').trim();
                    const [datePart, timePart = '00:00:00'] = dateStr.split(' ');
                    const [year, month, day] = datePart.split('-').map(Number);
                    const [hour, minute, second = 0] = timePart.split(':').map(Number);

                    // Criar data no horário local
                    const localDate = new Date(year, month - 1, day, hour, minute, second);

                    // Retornar no formato ISO sem timezone para FullCalendar interpretar como local
                    return localDate.getFullYear() + '-' +
                        String(localDate.getMonth() + 1).padStart(2, '0') + '-' +
                        String(localDate.getDate()).padStart(2, '0') + 'T' +
                        String(localDate.getHours()).padStart(2, '0') + ':' +
                        String(localDate.getMinutes()).padStart(2, '0') + ':' +
                        String(localDate.getSeconds()).padStart(2, '0');
                };

                const startFormatted = parseLocalDate(evento.startDatetime);
                const endFormatted = parseLocalDate(evento.endDatetime);

                // Objeto base do evento
                const eventoFormatado = {
                    id: evento.id,
                    title: evento.title,
                    start: startFormatted,
                    end: endFormatted,
                    color: getEventColor(evento),
                    extendedProps: {
                        patientId: evento.patientId,
                        therapistId: evento.therapistId,
                        planId: evento.planId,
                        jobPositionId: evento.jobPositionId,
                        note: evento.note,
                        appointmentType: evento.appointmentType,
                        absenceType: evento.absenceType,
                        chargeable: evento.chargeable,
                        allowReschedule: evento.allowReschedule,
                        therapists: evento.therapists,
                        value: evento.value,
                        therapist: evento.therapist,
                        patient: evento.patient
                    },
                };

                // Aplicar borda vermelha apenas para faltas
                if (evento.absenceType === 1 || evento.absenceType === 2) {
                    eventoFormatado.borderColor = 'red';
                }

                return eventoFormatado;
            });

            // Armazena no cache antes de definir no state
            eventosCache.current.set(fetchKey, eventosFormatados);

            setEventos(eventosFormatados);
            return eventosFormatados;

        } catch (error) {
            console.error('❌ Erro ao buscar eventos:', error);
            showNotification('danger', 'Erro ao carregar eventos do calendário');
            setEventos([]);
            return [];
        } finally {
            isFetching.current = false;
            setLoading(false);
        }
    }, [showNotification]);

    // Função para determinar a cor do evento
    const getEventColor = (evento) => {
        const tipo = evento.appointmentType;

        if (tipo === 2) {
            return '#000'; // Avulso = preto
        } else if (tipo === 0) {
            return '#eb6737'; // Livre = laranja
        } else {
            return evento.therapist?.color || '#3788d8'; // Sessão = cor da terapeuta ou azul padrão
        }
    };

    // Função para limpar o cache
    const limparCache = useCallback(() => {
        eventosCache.current.clear();
        lastFetchKey.current = null;
        isFetching.current = false;
        lastDatesSet.current = null;
    }, []);

    // Função para forçar atualização do calendário
    const forcarAtualizacaoCalendario = useCallback(async () => {
        if (!calendarRef.current) {
            return;
        }

        try {
            const calendarApi = calendarRef.current.getApi();
            const currentDate = calendarApi.getDate();
            const view = calendarApi.view;
            const viewType = view.type;

            // Determinar o tipo de busca baseado na visualização atual
            if (viewType === 'dayGridMonth') {
                // Busca mensal
                const { month, year } = getMonthYear(currentDate);

                await buscarEventosAPI({
                    type: 'monthly',
                    month: month,
                    year: year
                }, true); // forceRefresh = true
            } else if (viewType === 'timeGridDay') {
                // Busca diária

                await buscarEventosAPI({
                    type: 'daily',
                    date: currentDate
                }, true); // forceRefresh = true
            } else {
                // Busca semanal/período
                const startDate = new Date(view.currentStart);
                const endDate = new Date(view.currentEnd);

                await buscarEventosAPI({
                    type: 'period',
                    startDate: startDate,
                    endDate: endDate
                }, true); // forceRefresh = true
            }

        } catch (error) {
            console.error('❌ Erro ao forçar atualização do calendário:', error);
        }
    }, [buscarEventosAPI, getMonthYear, formatDateForAPI]);

    // Função para obter estatísticas do cache
    const getCacheStats = useCallback(() => {
        const size = eventosCache.current.size;
        const keys = Array.from(eventosCache.current.keys());
        return { size, keys };
    }, []);

    // Callback executado quando o calendário muda de período (navegação)
    const handleDatesSet = useCallback((dateInfo) => {
        const startDate = dateInfo.start;
        const endDate = dateInfo.end;
        const viewType = dateInfo.view.type;

        // Criar uma chave única para este datesSet
        const datesSetKey = `${viewType}_${startDate.getTime()}_${endDate.getTime()}`;

        // Proteção contra loops - se é o mesmo datesSet, ignorar
        if (lastDatesSet.current === datesSetKey) {
            return;
        }

        lastDatesSet.current = datesSetKey;

        // Atualiza a visualização atual
        currentView.current = viewType;

        let newKey;
        if (viewType === 'dayGridMonth') {
            // Para visualização mensal, vamos usar uma abordagem mais precisa
            // Pegar o 15º dia do período para garantir que estamos no mês correto
            const fifteenthDay = new Date(startDate);
            fifteenthDay.setDate(15);

            // Se o 15º dia estiver fora do período, usar a data média
            if (fifteenthDay < startDate || fifteenthDay > endDate) {
                fifteenthDay.setTime(startDate.getTime() + (endDate.getTime() - startDate.getTime()) / 2);
            }

            const { month, year } = getMonthYear(fifteenthDay);
            newKey = `month_${year}_${month}`;
        } else if (viewType === 'timeGridDay') {
            // Para visualização diária, usar a API do FullCalendar para obter a data atual
            let dayDate;
            if (calendarRef.current) {
                const calendarApi = calendarRef.current.getApi();
                dayDate = calendarApi.getDate();
            } else {
                // Fallback para startDate se a API não estiver disponível
                dayDate = new Date(startDate);
            }

            const dateFormatted = formatDateForAPI(dayDate);
            newKey = `daily_${dateFormatted}`;

        } else {
            // Outras visualizações - usa período
            const startFormatted = formatDateForAPI(startDate);
            const endFormatted = formatDateForAPI(endDate);
            newKey = `period_${startFormatted}_${endFormatted}`;

        }


        // Só atualiza se for um período diferente
        if (lastFetchKey.current !== newKey) {

            // Usar setTimeout para debounce
            setTimeout(() => {
                if (viewType === 'dayGridMonth') {
                    const fifteenthDay = new Date(startDate);
                    fifteenthDay.setDate(15);

                    if (fifteenthDay < startDate || fifteenthDay > endDate) {
                        fifteenthDay.setTime(startDate.getTime() + (endDate.getTime() - startDate.getTime()) / 2);
                    }

                    const { month, year } = getMonthYear(fifteenthDay);

                    // Atualiza o range para triggerar o useEffect
                    setCurrentRange({
                        start: startDate,
                        end: endDate,
                        viewType: 'monthly',
                        month,
                        year,
                        referenceDate: fifteenthDay,
                        timestamp: Date.now() // Adiciona timestamp para forçar re-render se necessário
                    });
                } else if (viewType === 'timeGridDay') {
                    let dayDate;
                    if (calendarRef.current) {
                        const calendarApi = calendarRef.current.getApi();
                        dayDate = calendarApi.getDate();
                    } else {
                        dayDate = new Date(startDate);
                    }

                    // Atualiza o range para triggerar o useEffect
                    setCurrentRange({
                        start: startDate,
                        end: endDate,
                        viewType: 'daily',
                        date: dayDate,
                        dateFormatted: formatDateForAPI(dayDate),
                        timestamp: Date.now()
                    });
                } else {
                    const startFormatted = formatDateForAPI(startDate);
                    const endFormatted = formatDateForAPI(endDate);

                    // Atualiza o range para triggerar o useEffect
                    setCurrentRange({
                        start: startDate,
                        end: endDate,
                        viewType: 'period',
                        startDate,
                        endDate,
                        timestamp: Date.now()
                    });
                }
            }, 100); // Debounce de 100ms
        } else {
        }
    }, []);

    // Callback para detectar mudança de visualização
    const handleViewChange = useCallback((viewInfo) => {
        currentView.current = viewInfo.view.type;

        // Se mudou para visualização mensal, força uma nova busca
        if (viewInfo.view.type === 'dayGridMonth') {
            const currentMonthYear = getCurrentMonthYear();
            if (currentMonthYear) {

                const newKey = `month_${currentMonthYear.year}_${currentMonthYear.month}`;
                if (lastFetchKey.current !== newKey) {
                    setCurrentRange({
                        viewType: 'monthly',
                        month: currentMonthYear.month,
                        year: currentMonthYear.year
                    });
                }
            }
        }
    }, [getCurrentMonthYear]);

    // Callback para clique em data
    const handleDateClick = useCallback((info) => {
        // Fechar qualquer popover "ver mais" aberto
        const popover = document.querySelector('.fc-popover');
        if (popover) {
            popover.remove();
        }

        // Se estiver na visualização Mês e clicar em um dia, mudar para visualização Dia
        if (currentView.current === 'dayGridMonth' && calendarRef.current) {
            const dataClicada = new Date(info.dateStr + 'T00:00:00');

            // Mudar para visualização Dia
            const calendarApi = calendarRef.current.getApi();
            calendarApi.changeView('timeGridDay', dataClicada);

            return; // Sair da função para não abrir o modal
        }

        let dataInicial, dataFinal, horaInicial, horaFinal;

        // Verificar se é um clique com horário específico (timeGrid) ou data simples (dayGrid)
        if (info.date) {
            // info.date contém a data/hora exata do clique
            const dataHoraClique = new Date(info.date);

            // Extrair data local (YYYY-MM-DD) - SEM usar toISOString para evitar problemas de timezone
            const ano = dataHoraClique.getFullYear();
            const mes = String(dataHoraClique.getMonth() + 1).padStart(2, '0');
            const dia = String(dataHoraClique.getDate()).padStart(2, '0');
            dataInicial = `${ano}-${mes}-${dia}`;
            dataFinal = dataInicial;

            // Extrair hora local (HH:MM)
            horaInicial = dataHoraClique.toTimeString().slice(0, 5);

            // Hora final = hora inicial + 1 hora
            const dataHoraFim = new Date(dataHoraClique);
            dataHoraFim.setHours(dataHoraFim.getHours() + 1);
            horaFinal = dataHoraFim.toTimeString().slice(0, 5);
        } else {
            // Fallback para dateStr (provavelmente dayGridMonth)
            dataInicial = info.dateStr;
            dataFinal = dataInicial;
            horaInicial = arredondarHoraAtual();
            horaFinal = horaFinalPadrao();
        }

        // Resetar estados do modal
        setDataInicio(dataInicial);
        setDataFim(dataFinal);
        setHoraInicio(horaInicial);
        setHoraFim(horaFinal);
        setTitulo("");
        setAllDay(false);
        setTipoAgendAdd(1);
        setReposicao(0);
        setConsideraValor(0);

        // Resetar seleções
        setPacienteSearch('');
        setPacienteSelecionado(null);
        setTerapeutaSearch('');
        setTerapeutaSelecionado(null);
        setOutrasTerapeutas([]);
        setIdsTerapeutasExtras([]);
        setAgendamentosRelacionados([]);
        setPlanosPaciente([]);
        setFuncoesTerapeuta([]);

        // Resetar form
        setFormAddEvent({
            startDatetime: "",
            endDateTime: "",
            note: "",
            tipo: 1,
            title: "",
            patientId: "",
            planId: "",
            therapistId: "",
            jobPositionId: "",
            roomId: "",
            value: "",
        });

        // Abrir modal
        setShowModalAdd(true);
    }, []);

    // Callback para clique em evento
    const handleEventClick = useCallback(async (info) => {

        // Fechar qualquer popover "ver mais" aberto
        const popover = document.querySelector('.fc-popover');
        if (popover) {
            popover.remove();
        }

        const eventId = info.event.id;

        // Resetar estados
        setAgendamentosRelacionados([]);
        setOutraTerapSearch("");
        setOutrasTerapeutas([]);
        setIdsTerapeutasExtras([]);
        setFaturada(0);
        setInfoFalta(null);
        setInfoCriacao(null);
        setGeraReposicao(0);
        setReposicao(0);
        setPacienteFoiSelecionadoExternamente(false);
        setTerapeutaFoiSelecionadoExternamente(false);

        try {
            // SEMPRE buscar dados atualizados da API por ID
            const response = await authFetch(`appointment/${eventId}`);
            if (!response.ok) throw new Error("Erro ao buscar dados do evento");

            const eventData = await response.json();

            // Preencher dados básicos com dados da API
            setIdEventoSelecionado(eventData.id);
            setTitulo(eventData.title);

            // Converter datas da API usando timezone local
            const startDate = new Date(eventData.startDatetime);
            const endDate = new Date(eventData.endDatetime);

            // Extrair data local sem problemas de timezone
            const dataInicioLocal = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
            const dataFimLocal = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

            setDataInicio(dataInicioLocal);
            setHoraInicio(startDate.toTimeString().substring(0, 5));
            setDataFim(dataFimLocal);
            setHoraFim(endDate.toTimeString().substring(0, 5));
            setAllDay(eventData.isAllDay);

            setTipoAgendEdit(eventData.appointmentType);

            setInfoCriacao({
                createdAt: eventData.createdAt || null,
                createdByName: eventData.createdByUser?.name || null,
            });

            // Configurar tipos de falta
            if (eventData.absenceType === 1 || eventData.absenceType === 2) {
                setFalta(1);
                if (eventData.absenceType === 2) {
                    setFaturada(1);
                }
                setInfoFalta({
                    note: eventData.absenceNote || null,
                    markedByName: eventData.absenceMarkedByUser?.name || null,
                    markedAt: eventData.absenceMarkedAt || null,
                });
            } else {
                setFalta(0);
                setInfoFalta(null);
            }

            if (eventData.absenceType === 3) {
                setReposicao(1);
            }

            setConsideraValor(eventData.chargeable ? 1 : 0);
            setGeraReposicao(eventData.allowReschedule ? 1 : 0);

            // Preencher dados do paciente se existir
            if (eventData.appointmentType !== 0 && eventData.patient) {
                setPacienteSearch(eventData.patient.name);
                setPacienteSelecionado(eventData.patient);
                setPacienteFoiSelecionadoExternamente(true);

                // Buscar planos do paciente
                if (eventData.appointmentType === 1) {
                    buscarPlanosEdicao(eventData.patient.id);
                }
            }

            // Preencher dados do terapeuta se existir
            if (eventData.appointmentType !== 0 && eventData.therapist) {
                setTerapeutaSearch(eventData.therapist.name);
                setTerapeutaSelecionado(eventData.therapist);
                setTerapeutaFoiSelecionadoExternamente(true);

                // Buscar funções do terapeuta
                buscarFuncoes(eventData.therapist.id);
            }

            if (eventData.therapists?.length > 0) {
                setAgendamentosRelacionados(eventData.therapists);
                setOutrasTerapeutas(eventData.therapists);
                setIdsTerapeutasExtras(eventData.therapists.map(t => t.therapistId));
            }

            // Preencher form de edição
            setFormEditEvent({
                id: eventData.id,
                patientId: eventData.patientId,
                therapistId: eventData.therapistId,
                jobPositionId: eventData.jobPositionId,
                planId: eventData.planId,
                note: eventData.note || "",
                value: eventData.value ? formatarParaMoeda((eventData.value ?? 0).toFixed(2).toString()) : "",
                therapists: eventData.therapists || []
            });

            // Abrir modal de edição
            setShowModalEdit(true);

        } catch (error) {
            console.error('❌ Erro ao buscar dados do evento:', error);
            showNotification('danger', 'Erro ao carregar dados do evento');
        }
    }, []);

    // Effect para buscar eventos quando o período muda
    useEffect(() => {
        if (!currentRange || !isInitialized.current) {
            return;
        }

        // Proteção adicional contra loops
        if (isFetching.current) {
            return;
        }


        // Usar setTimeout para debounce e evitar chamadas síncronas
        const timeoutId = setTimeout(() => {
            // Verificar novamente se não está fazendo fetch
            if (isFetching.current) {
                return;
            }

            if (currentRange.viewType === 'monthly') {
                // Busca mensal
                buscarEventosAPI({
                    type: 'monthly',
                    month: currentRange.month,
                    year: currentRange.year
                });
            } else if (currentRange.viewType === 'daily') {
                // Busca diária
                buscarEventosAPI({
                    type: 'daily',
                    date: currentRange.date
                });
            } else {
                // Busca por período
                buscarEventosAPI({
                    type: 'period',
                    startDate: currentRange.startDate,
                    endDate: currentRange.endDate
                });
            }
        }, 150); // Delay maior para evitar múltiplas chamadas

        return () => {
            clearTimeout(timeoutId);
        };
    }, [currentRange?.timestamp]); // Usar apenas timestamp como dependência

    // Função para adicionar ícone nas células dos dias após renderização
    const dayCellDidMount = useCallback((info) => {
        // Só adicionar ícone na visualização mensal
        if (info.view.type !== 'dayGridMonth') {
            return;
        }

        const dateStr = info.date.toISOString().split('T')[0];

        // Centralizar o número do dia horizontalmente (mantendo altura original)
        const dayNumberElement = info.el.querySelector('.fc-daygrid-day-number');
        if (dayNumberElement) {
            Object.assign(dayNumberElement.style, {
                display: 'block',
                textAlign: 'center',
                width: '100%',
                fontWeight: 'bold',
                fontSize: '16px'
            });
        }

        // Role 6 não pode adicionar eventos
        if (isReadOnly) {
            return;
        }

        // Criar o ícone de adicionar evento
        const addButton = document.createElement('button');
        addButton.innerHTML = '+';
        addButton.title = `Adicionar evento em ${info.date.toLocaleDateString('pt-BR')}`;

        // Estilos do botão
        Object.assign(addButton.style, {
            position: 'absolute',
            top: '5px',
            right: '5px',
            background: 'linear-gradient(135deg, #47a0be, #2c7594)',
            border: 'none',
            borderRadius: '50%',
            width: '22px',
            height: '22px',
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '10',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            transition: 'all 0.2s ease',
            opacity: '0.8'
        });

        // Event listeners para hover
        addButton.addEventListener('mouseenter', () => {
            addButton.style.opacity = '1';
            addButton.style.transform = 'scale(1.1)';
            addButton.style.boxShadow = '0 3px 6px rgba(0,0,0,0.3)';
        });

        addButton.addEventListener('mouseleave', () => {
            addButton.style.opacity = '0.8';
            addButton.style.transform = 'scale(1)';
            addButton.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
        });

        // Event listener para click
        addButton.addEventListener('click', (e) => {
            e.stopPropagation();
            handleAddEventToDay(dateStr, e);
        });

        // Garantir que a célula tenha position relative
        info.el.style.position = 'relative';

        // Adicionar o botão à célula
        info.el.appendChild(addButton);
    }, [isReadOnly]);

    // Handler para fechar popover "ver mais" quando clicar em qualquer lugar
    const handleMoreLinkClick = useCallback((info) => {
        // Configurar popover "ver mais" com rolagem e fechar ao clicar fora
        setTimeout(() => {
            // Adicionar rolagem ao popover
            const popover = document.querySelector('.fc-popover');
            if (popover) {
                popover.style.maxHeight = '300px';
                popover.style.overflowY = 'auto';
                popover.style.zIndex = '1000'; // Garantir que não sobreponha modais

                // Fechar popover ao clicar fora
                const handleClickOutside = (event) => {
                    if (!popover.contains(event.target)) {
                        popover.remove();
                        document.removeEventListener('click', handleClickOutside);
                    }
                };

                // Adicionar listener após um pequeno delay
                setTimeout(() => {
                    document.addEventListener('click', handleClickOutside);
                }, 100);
            }
        }, 50);

        return 'popover'; // Manter comportamento padrão do popover
    }, []);

    // Memoizar configurações do calendário para evitar re-renderizações
    const calendarConfig = useMemo(() => ({
        plugins: [dayGridPlugin, interactionPlugin, TimeGridPlugin, bootstrap5Plugin, listPlugin],
        headerToolbar: {
            start: 'prev,next today',
            center: 'title',
            end: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
        },
        locales: [ptBrLocale],
        locale: "pt-br",
        themeSystem: 'bootstrap5',
        buttonIcons: true,
        weekNumbers: false,
        navLinks: true,
        editable: !isReadOnly, // Desabilitar edição para role 6
        dayMaxEvents: true,
        dayMaxEventRows: 3,
        moreLinkClick: handleMoreLinkClick,
        slotDuration: '00:30:00',
        initialView: "timeGridWeek",
        hiddenDays: [0, 6],
        businessHours: {
            daysOfWeek: [1, 2, 3, 4, 5],
            startTime: '08:00',
            endTime: '18:00',
        },
        selectable: !isReadOnly, // Desabilitar seleção para role 6
        selectMirror: !isReadOnly,
        selectOverlap: false,
        dayCellDidMount: dayCellDidMount,
    }), [dayCellDidMount, handleMoreLinkClick, isReadOnly]);

    // Effect para carregar dados iniciais
    useEffect(() => {

        // Pequeno delay para garantir que o calendário esteja totalmente inicializado
        const initTimeout = setTimeout(() => {
            isInitialized.current = true;

            // Fazer busca inicial para a semana atual se o currentRange ainda não foi definido
            if (!currentRange && calendarRef.current) {
                const calendarApi = calendarRef.current.getApi();
                const currentDate = calendarApi.getDate();
                const view = calendarApi.view;

                // Simular o handleDatesSet para a visualização inicial
                const startDate = new Date(view.currentStart);
                const endDate = new Date(view.currentEnd);
                const viewType = view.type;


                if (viewType === 'timeGridWeek') {
                    const startFormatted = formatDateForAPI(startDate);
                    const endFormatted = formatDateForAPI(endDate);

                    setCurrentRange({
                        start: startDate,
                        end: endDate,
                        viewType: 'period',
                        startDate,
                        endDate,
                        timestamp: Date.now()
                    });
                }
            }
        }, 500); // Delay maior para garantir que o calendário esteja pronto

        // Cleanup do cache ao desmontar o componente
        return () => {
            clearTimeout(initTimeout);
            eventosCache.current.clear();
        };
    }, []);

    // useEffect para fechar popover quando modal abrir
    useEffect(() => {
        if (showModalAdd || showModalEdit || showModalConfirmacao) {
            // Fechar qualquer popover aberto quando modal for aberto
            const popover = document.querySelector('.fc-popover');
            if (popover) {
                popover.remove();
            }
        }
    }, [showModalAdd, showModalEdit, showModalConfirmacao]);

    // useEffect para fechar popover quando modal abrir
    useEffect(() => {
        if (showModalConfirmacao)
        setShowModalEdit(false);
    }, [showModalConfirmacao]);

    // Funções para o modal de edição
    const alterFalta = (e) => {
        setFalta(e.target.checked ? 1 : 0);
    };

    const alterFaturada = (e) => {
        setFaturada(e.target.checked ? 1 : 0);
    };

    const alterGeraReposicao = (e) => {
        setGeraReposicao(e.target.checked ? 1 : 0);
    };

    const handleInputChangeEdit = (e) => {
        const { name, value, id } = e.target;
        let novoValor = value;

        if (id === "valorEdit") {
            novoValor = formatarParaMoeda(value);
        }

        setFormEditEvent((prev) => ({
            ...prev,
            [name]: novoValor,
        }));

        // Se o plano foi alterado, atualizar o título
        if (name === "planId") {
            // Atualizar título imediatamente com o novo valor do plano
            if (tipoAgendEdit === 1 && pacienteSelecionado && novoValor) {
                const planoSelecionado = planosPaciente.find(p => p.plan.id == novoValor);
                if (planoSelecionado) {
                    setTitulo(`${pacienteSelecionado.name} - ${planoSelecionado.plan.name}`);
                }
            }
        }
    };

    const formatarParaMoeda = (valor) => {
        const numeroLimpo = valor.replace(/\D/g, '');
        const numeroFormatado = (numeroLimpo / 100).toFixed(2);
        return numeroFormatado.replace('.', ',');
    };

    // Função para criar data local sem problemas de timezone
    const createLocalDateTime = (dateStr, timeStr) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        const [hour, minute] = timeStr.split(':').map(Number);

        // Criar data no timezone local
        const localDate = new Date(year, month - 1, day, hour, minute, 0);


        return localDate;
    };

    const formatDateTime = (date) => {
        // Garantir que estamos sempre usando o horário local
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        const formatted = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

        return formatted;
    };

    // Função removida - dados agora são buscados diretamente no handleEventClick

    // Buscar planos do paciente para edição
    const buscarPlanosEdicao = async (pacienteId) => {
        try {
            const response = await authFetch(`patient-plan/patient/${pacienteId}`);
            if (!response.ok) throw new Error('Erro ao buscar planos');
            const data = await response.json();
            setPlanosPaciente(data);
            return data;
        } catch (error) {
            console.error('Erro ao buscar planos do paciente:', error);
            setPlanosPaciente([]);
            return [];
        }
    };

    // Função para editar evento
    const editarEvento = async (e) => {
        e.preventDefault();

        const dataInicioCompleta = createLocalDateTime(dataInicio, horaInicio);
        const dataFimCompleta = createLocalDateTime(dataFim, horaFim);

        let tipoFalta = 0;

        if (falta === 0) {
            tipoFalta = 0;
            if (reposicao === 1) {
                tipoFalta = 3;
            }
        } else {
            if (falta === 1) {
                tipoFalta = 1;
                if (faturada === 1) {
                    tipoFalta = 2;
                }
            }
        }

        // Payload base comum a todos os tipos
        const payload = {
            id: idEventoSelecionado,
            patientId: formEditEvent.patientId || null,
            planId: formEditEvent.planId || null,
            therapistId: formEditEvent.therapistId || null,
            jobPositionId: formEditEvent.jobPositionId || null,
            roomId: null,
            note: formEditEvent.note,
            startDatetime: formatDateTime(dataInicioCompleta),
            endDatetime: formatDateTime(dataFimCompleta),
            title: titulo,
            isAllDay: Boolean(allDay),
            color: terapeutaSelecionado?.color || "#eb6737",
            appointmentType: tipoAgendEdit,
            absenceType: tipoFalta,
            allowReschedule: Boolean(geraReposicao),
            chargeable: Boolean(consideraValor),
            therapists: outrasTerapeutas.map(t => ({
                therapistId: Number(t.therapistId || t.id),
                jobPositionId: Number(t.selectedFuncaoId || t.jobPositionId),
            })),
        };


        // Ajustes específicos
        if (tipoAgendEdit === 0) {
            payload.patientId = null;
            payload.planId = null;
            payload.therapistId = null;
            payload.jobPositionId = null;
            payload.color = "#eb6737";
            payload.absenceType = 0;
            payload.chargeable = false;
            payload.allowReschedule = false;
            payload.therapists = [];
        }

        if (tipoAgendEdit === 2) {
            payload.color = "#000";
            payload.value = formEditEvent.value.replace(/\./g, "").replace(",", ".");
        }

        try {
            const res = await authFetch('appointment', {
                method: 'PUT',
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error('Erro ao editar o evento');

            setShowModalEdit(false);

            // Limpar completamente o cache
            limparCache();

            // Forçar nova busca para o período atual
            await forcarAtualizacaoCalendario();

            showNotification('success', 'Evento Atualizado!');

        } catch (err) {
            console.error(err);
            showNotification('danger', 'Erro ao editar o evento');
        }

    };

    // Handler para quando um evento é arrastado no calendário
    const handleEventDrop = useCallback(async (info) => {
        // Role 6 não pode arrastar eventos
        if (isReadOnly) {
            info.revert();
            return;
        }

        const eventId = info.event.id;
        const newStart = info.event.start;
        // O FullCalendar preserva a duração ao arrastar, então sempre terá um end
        const newEnd = info.event.end || new Date(newStart.getTime() + 60 * 60 * 1000);

        try {
            // Buscar dados atuais do evento da API
            const response = await authFetch(`appointment/${eventId}`);
            if (!response.ok) throw new Error("Erro ao buscar dados do evento");

            const eventData = await response.json();

            // Criar payload com os dados atuais, mas com novas datas
            const payload = {
                id: eventData.id,
                patientId: eventData.patientId,
                planId: eventData.planId,
                therapistId: eventData.therapistId,
                jobPositionId: eventData.jobPositionId,
                roomId: eventData.roomId,
                note: eventData.note || "",
                startDatetime: formatDateTime(newStart),
                endDatetime: formatDateTime(newEnd),
                title: eventData.title,
                isAllDay: eventData.isAllDay || false,
                color: eventData.color || "#eb6737",
                appointmentType: eventData.appointmentType,
                absenceType: eventData.absenceType || 0,
                allowReschedule: eventData.allowReschedule || false,
                chargeable: eventData.chargeable || false,
                therapists: eventData.therapists || [],
            };

            // Se for tipo avulso, incluir o valor
            if (eventData.appointmentType === 2 && eventData.value) {
                payload.value = eventData.value;
            }

            // Atualizar evento na API
            const updateResponse = await authFetch('appointment', {
                method: 'PUT',
                body: JSON.stringify(payload),
            });

            if (!updateResponse.ok) {
                throw new Error('Erro ao atualizar o evento');
            }

            // Limpar cache e atualizar calendário
            limparCache();
            await forcarAtualizacaoCalendario();

            showNotification('success', 'Evento atualizado com sucesso!');

        } catch (error) {
            console.error('Erro ao atualizar evento após arrastar:', error);
            
            // Reverter a mudança no calendário
            info.revert();
            
            showNotification('danger', 'Erro ao atualizar o evento. Mudanças revertidas.');
        }
    }, [showNotification, limparCache, forcarAtualizacaoCalendario, isReadOnly]);

    // Função para confirmar exclusão do evento
    const confirmarExclusaoEvento = async () => {
        if (!eventoParaExcluir) return;

        try {
            const res = await authFetch(`appointment/${eventoParaExcluir}`, {
                method: 'DELETE',
            });

            if (!res.ok) throw new Error('Erro ao excluir o evento');

            showNotification('success', 'Evento excluído com sucesso');

            // Limpar completamente o cache
            limparCache();

            // Forçar nova busca para o período atual
            await forcarAtualizacaoCalendario();

            // Fechar ambos os modais
            setShowModalConfirmacao(false);
            setShowModalEdit(false);
            setEventoParaExcluir(null);
        } catch (err) {
            console.error(err);
            showNotification('danger', 'Erro ao excluir o evento');
            setShowModalConfirmacao(false);
        }
    };

    // Effects para desabilitar campos conforme regras
    useEffect(() => {
        if (falta === 0 || falta === false) {
            setGeraReposicao(0);
            setDisabledGeraRepo(true);
        } else {
            setDisabledGeraRepo(false);
        }
    }, [falta]);

    useEffect(() => {
        if (reposicao === 1 || reposicao === true) {
            setFalta(0);
            setFaturada(0);
            setDisabledFalta(true);
            setDisabledFaturada(true);
        } else {
            setDisabledFalta(false);
            setDisabledFaturada(false);
        }
    }, [reposicao]);

    // Função para lidar com clique no ícone de adicionar evento em um dia específico
    const handleAddEventToDay = useCallback((dateStr, event) => {
        event.stopPropagation(); // Evitar que o clique no dia seja disparado

        // Configurar data do evento
        const dataInicial = dateStr;
        const dataFinal = dateStr;
        const horaInicial = arredondarHoraAtual();
        const horaFinal = horaFinalPadrao();

        // Resetar estados do modal
        setDataInicio(dataInicial);
        setDataFim(dataFinal);
        setHoraInicio(horaInicial);
        setHoraFim(horaFinal);
        setTitulo("");
        setAllDay(false);
        setTipoAgendAdd(1);
        setReposicao(0);
        setConsideraValor(0);

        // Resetar seleções
        setPacienteSearch('');
        setPacienteSelecionado(null);
        setTerapeutaSearch('');
        setTerapeutaSelecionado(null);
        setOutrasTerapeutas([]);
        setIdsTerapeutasExtras([]);
        setAgendamentosRelacionados([]);
        setPlanosPaciente([]);
        setFuncoesTerapeuta([]);

        // Resetar form
        setFormAddEvent({
            startDatetime: "",
            endDateTime: "",
            note: "",
            tipo: 1,
            title: "",
            patientId: "",
            planId: "",
            therapistId: "",
            jobPositionId: "",
            roomId: "",
            value: "",
        });

        // Abrir modal
        setShowModalAdd(true);
    }, []);

    // ===================== FILTROS =====================

    // Controle de "selecionar todos" - Terapeutas
    useEffect(() => {
        // Removed the automatic state calculation to respect user clicks
        // Now the state is controlled purely by the filtrarTerapeuta function
    }, []);

    // Controle de "selecionar todos" - Pacientes
    useEffect(() => {
        // Removed the automatic state calculation to respect user clicks
        // Now the state is controlled purely by the filtrarPaciente function
    }, []);

    // Controle de "selecionar todos" - Tipos
    useEffect(() => {
        // Removed the automatic state calculation to respect user clicks
        // Now the state is controlled purely by the filtrarTipo function
    }, []);

    // Função para filtrar terapeutas
    const filtrarTerapeuta = (terapeuta = null) => {
        if (terapeuta === null) {
            if (todosTerapSelecionados) {
                // Se todos estão selecionados, desseleciona todos
                setTerapeutasDesselecionadosFiltro(terapeutasFiltro.map(t => t.id));
            } else {
                // Se nem todos estão selecionados, seleciona todos
                setTerapeutasDesselecionadosFiltro([]);
            }
            setTodosTerapSelecionados(!todosTerapSelecionados);
        } else {
            setTerapeutasDesselecionadosFiltro(prev => {
                const isSelected = !prev.includes(terapeuta.id);
                if (isSelected) {
                    // Se está selecionado, desseleciona
                    const newDesselecionados = [...prev, terapeuta.id];
                    setTodosTerapSelecionados(false);
                    return newDesselecionados;
                } else {
                    // Se está desselecionado, seleciona
                    const newDesselecionados = prev.filter(id => id !== terapeuta.id);
                    setTodosTerapSelecionados(newDesselecionados.length === 0);
                    return newDesselecionados;
                }
            });
        }
    };

    // Função para filtrar pacientes
    const filtrarPaciente = (paciente = null) => {
        if (paciente === null) {
            if (todosPacientesSelecionados) {
                // Se todos estão selecionados, desseleciona todos
                setPacientesDesselecionadosFiltro(pacientesFiltro.map(p => p.id));
            } else {
                // Se nem todos estão selecionados, seleciona todos
                setPacientesDesselecionadosFiltro([]);
            }
            setTodosPacientesSelecionados(!todosPacientesSelecionados);
        } else {
            setPacientesDesselecionadosFiltro(prev => {
                const isSelected = !prev.includes(paciente.id);
                if (isSelected) {
                    // Se está selecionado, desseleciona
                    const newDesselecionados = [...prev, paciente.id];
                    setTodosPacientesSelecionados(false);
                    return newDesselecionados;
                } else {
                    // Se está desselecionado, seleciona
                    const newDesselecionados = prev.filter(id => id !== paciente.id);
                    setTodosPacientesSelecionados(newDesselecionados.length === 0);
                    return newDesselecionados;
                }
            });
        }
    };

    // Função para filtrar tipos
    const filtrarTipo = (tipo = null) => {
        if (tipo === null) {
            // Toggle "selecionar todos"
            if (todosTiposSelecionados) {
                // Se estava tudo selecionado, apenas desativa o "selecionar todos"
                setTodosTiposSelecionados(false);
                setTiposDesselecionadosFiltro(tiposAgendamento.map(t => t.id));
            } else {
                // Se não estava tudo selecionado, ativa o "selecionar todos" e limpa a lista de desselecionados
                setTodosTiposSelecionados(true);
                setTiposDesselecionadosFiltro([]);
            }
        } else {
            // Toggle tipo individual
            const tipoId = parseInt(tipo.id, 10); // Garante que o ID seja numérico
            setTiposDesselecionadosFiltro(prev => {
                const prevNumeric = prev.map(id => parseInt(id, 10)); // Converte todos os IDs para número
                if (prevNumeric.includes(tipoId)) {
                    // Se estava desselecionado, remove da lista de desselecionados
                    const newDesselecionados = prevNumeric.filter(id => id !== tipoId);
                    // Se não há mais tipos desselecionados, marca "todos selecionados" como true
                    if (newDesselecionados.length === 0) {
                        setTodosTiposSelecionados(true);
                    }
                    return newDesselecionados;
                } else {
                    // Se estava selecionado, adiciona à lista de desselecionados
                    const newDesselecionados = [...prevNumeric, tipoId];
                    setTodosTiposSelecionados(false);
                    return newDesselecionados;
                }
            });
        }
    };

    // Função para aplicar filtros aos eventos
    const filtrarEventos = (eventos) => {
        const eventosFiltrados = eventos.filter(evento => {
            const { therapistId, patientId, appointmentType } = evento.extendedProps;
            
            // Converte o tipo para número e verifica se está desselecionado
            const tipoEvento = parseInt(appointmentType, 10);
            const tipoDesselecionado = tiposDesselecionadosFiltro.some(id => 
                parseInt(id, 10) === tipoEvento
            );

            // Se o tipo está desselecionado, esconde o evento
            if (tipoDesselecionado) {
                return false;
            }

            // Eventos sem paciente sempre são mostrados
            if (!patientId) {
                return true;
            }

            // Se o terapeuta está desselecionado, esconde o evento
            if (terapeutasDesselecionadosFiltro.includes(therapistId)) {
                return false;
            }

            // Se o paciente está desselecionado, esconde o evento
            if (pacientesDesselecionadosFiltro.includes(patientId)) {
                return false;
            }

            // Se passou por todos os filtros, mostra o evento
            return true;
        });

        return eventosFiltrados;
    };

    // Buscar terapeutas para filtro
    useEffect(() => {
        const fetchTerapeutasFiltro = async () => {
            try {
                const response = await authFetch('therapist?enabled=true');
                if (!response.ok) throw new Error("Erro ao buscar Terapeutas");
                const data = await response.json();
                const terapeutasOrdenados = data.sort((a, b) => a.name.localeCompare(b.name));
                setTerapeutasFiltro(terapeutasOrdenados);
                setTerapeutasFiltroFiltradas(terapeutasOrdenados); // Inicializar lista filtrada
            } catch (err) {
                console.error(err.message);
            }
        };
        fetchTerapeutasFiltro();
    }, []);

    // Filtrar terapeutas conforme busca por nome
    useEffect(() => {
        if (buscaTerapeutaFiltro.trim() === '') {
            setTerapeutasFiltroFiltradas(terapeutasFiltro);
        } else {
            const terapeutasFiltradas = terapeutasFiltro.filter(terapeuta =>
                terapeuta.name.toLowerCase().includes(buscaTerapeutaFiltro.toLowerCase()) ||
                terapeuta.nickname.toLowerCase().includes(buscaTerapeutaFiltro.toLowerCase())
            );
            setTerapeutasFiltroFiltradas(terapeutasFiltradas);
        }
    }, [buscaTerapeutaFiltro, terapeutasFiltro]);

    // Buscar pacientes para filtro
    useEffect(() => {
        const fetchPacientesFiltro = async () => {
            try {
                const response = await authFetch('patient?enabled=true');
                if (!response.ok) throw new Error("Erro ao buscar Pacientes");
                const data = await response.json();
                const pacientesOrdenados = data.sort((a, b) => a.name.localeCompare(b.name));
                setPacientesFiltro(pacientesOrdenados);
                setPacientesFiltroFiltrados(pacientesOrdenados); // Inicializar lista filtrada
                // Selecionar todos os pacientes por padrão
                setPacientesSelecionadosFiltro(pacientesOrdenados.map(p => p.id));
            } catch (err) {
                console.error(err.message);
            }
        };
        fetchPacientesFiltro();
    }, []);

    // Filtrar pacientes conforme busca por nome
    useEffect(() => {
        if (buscaPacienteFiltro.trim() === '') {
            setPacientesFiltroFiltrados(pacientesFiltro);
        } else {
            const pacientesFiltrados = pacientesFiltro.filter(paciente =>
                paciente.name.toLowerCase().includes(buscaPacienteFiltro.toLowerCase())
            );
            setPacientesFiltroFiltrados(pacientesFiltrados);
        }
    }, [buscaPacienteFiltro, pacientesFiltro]);

    // Recarregar eventos quando filtros mudarem
    useEffect(() => {
        // Forçar recarregamento dos eventos aplicando filtros
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.refetchEvents();
        }
    }, [terapeutasSelecionadasFiltro, pacientesSelecionadosFiltro, tiposSelecionadosFiltro]);

    // ===================== FIM FILTROS =====================

    // ===================== FUNÇÕES DE REPLICAR =====================

    // Função para replicar dia
    const handleReplicarDia = async (e) => {
        e.preventDefault();

        try {
            const response = await authFetch(`appointment/replicate/day/${dataInicioRepli}/${dataFimRepli}`, {
                method: "POST",
            });

            if (!response.ok) {
                throw new Error(`Erro ao replicar dia: ${response.status}`);
            }

            setShowModalReplicarDia(false);
            showNotification('success', 'Dia replicado com sucesso!');

            // Limpar completamente o cache
            limparCache();

            // Forçar nova busca para o período atual
            await forcarAtualizacaoCalendario();

        } catch (error) {
            console.error(error);
            showNotification('danger', 'Erro ao replicar dia.');
        }
    };

    // Função auxiliar para converter semana para data de segunda-feira
    const getMondayFromWeekInput = (weekString) => {
        const [year, week] = weekString.split('-W').map(Number);
        const simple = new Date(year, 0, 1 + (week - 1) * 7);
        const dow = simple.getDay();
        const monday = new Date(simple);

        // Ajusta para segunda-feira
        if (dow <= 4) {
            monday.setDate(simple.getDate() - simple.getDay() + 1);
        } else {
            monday.setDate(simple.getDate() + 8 - simple.getDay());
        }

        // Retorna no formato YYYY-MM-DD
        return monday.toISOString().split('T')[0];
    };

    // Função para replicar semana
    const handleReplicarSemana = async (e) => {
        e.preventDefault();

        try {
            const semanaInicial = getMondayFromWeekInput(semOrigem);
            const semanaFinal = getMondayFromWeekInput(semDestino);

            const response = await authFetch(`appointment/replicate/week/${semanaInicial}/${semanaFinal}`, {
                method: "POST",
            });

            if (!response.ok) {
                throw new Error(`Erro ao replicar semana: ${response.status}`);
            }

            setShowModalReplicarSem(false);
            showNotification('success', 'Semana replicada com sucesso!');

            // Limpar completamente o cache
            limparCache();

            // Forçar nova busca para o período atual
            await forcarAtualizacaoCalendario();

        } catch (error) {
            console.error(error);
            showNotification('danger', 'Erro ao replicar semana.');
        }
    };

    // ===================== FIM FUNÇÕES DE REPLICAR =====================

    return (
        <div className="content">
            <div className="row">
                <div className="col-12">
                    <div className="mb-4">
                        <h3 className="font-weight-bolder mb-0">Calendário Novo</h3>

                    </div>
                </div>
            </div>

            {/* Filtros */}
            <div className="row mb-4">
                <div className="col-md-12">
                    <div className="card">
                        <div
                            className="card-header d-flex justify-content-between align-items-center"
                            style={{
                                cursor: 'pointer',
                                backgroundColor: '#f8f9fa',
                                borderBottom: filtrosExpandidos ? '1px solid #dee2e6' : 'none',
                                padding: '0.5rem 1rem'
                            }}
                            onClick={() => setFiltrosExpandidos(!filtrosExpandidos)}
                        >
                            <h6 className="mb-0 d-flex align-items-center">
                                <i className="fas fa-filter me-2" style={{ color: '#47a0be' }}></i>
                                Filtros e Ações
                                <small className="text-muted ms-2">
                                    ({eventos.length - filtrarEventos(eventos).length} eventos ocultos)
                                </small>
                            </h6>
                            <i
                                className={`fas ${filtrosExpandidos ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                                style={{
                                    color: '#47a0be',
                                    transition: 'transform 0.2s ease'
                                }}
                            ></i>
                        </div>

                        {filtrosExpandidos && (
                            <div className="card-body" style={{ paddingTop: '1rem', margin: '0 0.75rem' }}>
                                <div className="row">
                                    <div className="col-md-4">
                                        <h6>Terapeutas</h6>
                                        <div className="card container" style={{ height: "10em", overflowY: "auto" }}>
                                            {/* Campo de busca */}
                                            <div className="px-2 py-1">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Buscar terapeuta..."
                                                    value={buscaTerapeutaFiltro}
                                                    onChange={(e) => setBuscaTerapeutaFiltro(e.target.value)}
                                                    style={{ fontSize: '12px' }}
                                                />
                                            </div>
                                            <ul className="navbar-nav" style={{ padding: '0', margin: '0' }}>
                                                <li className="nav-item" style={{ marginBottom: '2px' }}>
                                                    <div className="filtro-link d-flex align-items-center"
                                                        style={{ cursor: 'pointer', padding: '0.25rem 0.5rem' }}
                                                        onClick={() => filtrarTerapeuta(null)}>
                                                        <div className="icon text-center me-2">
                                                            <i
                                                                className="fa-solid fa-circle-check"
                                                                style={{
                                                                    borderRadius: "50%",
                                                                    color: "#47a0be",
                                                                    backgroundColor: !todosTerapSelecionados ? "#47a0be" : "white",
                                                                    fontSize: '12px'
                                                                }}
                                                            ></i>
                                                        </div>
                                                        <span className="nav-link-text ms-1" style={{ color: "#47a0be", fontSize: '13px' }}>
                                                            Selecionar Todos
                                                        </span>
                                                    </div>
                                                </li>
                                            </ul>
                                            <ul className="navbar-nav" style={{ padding: '0', margin: '0' }}>
                                                {terapeutasFiltroFiltradas && terapeutasFiltroFiltradas.map((t) => (
                                                    <li className="nav-item" key={t.id} style={{ marginBottom: '2px' }}>
                                                        <div className="filtro-link d-flex align-items-center"
                                                            style={{ cursor: 'pointer', padding: '0.25rem 0.5rem' }}
                                                            onClick={() => filtrarTerapeuta(t)}>
                                                            <div className="icon text-center me-2">
                                                                <i className="fa-solid fa-circle-check" style={{
                                                                    borderRadius: "50%",
                                                                    color: `${t.color}`,
                                                                    backgroundColor: terapeutasDesselecionadosFiltro.includes(t.id) ? t.color : 'white',
                                                                    fontSize: '12px'
                                                                }}></i>
                                                            </div>
                                                            <span className="nav-link-text ms-1" style={{
                                                                borderRadius: "50%",
                                                                color: `${t.color}`,
                                                                fontSize: '13px'
                                                            }}>
                                                                {t.nickname}
                                                            </span>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <h6>Pacientes</h6>
                                        <div className="card container" style={{ height: "10em", overflowY: "auto" }}>
                                            {/* Campo de busca */}
                                            <div className="px-2 py-1">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Buscar paciente..."
                                                    value={buscaPacienteFiltro}
                                                    onChange={(e) => setBuscaPacienteFiltro(e.target.value)}
                                                    style={{ fontSize: '12px' }}
                                                />
                                            </div>
                                            <ul className="navbar-nav">
                                                <li className="nav-item" style={{ marginBottom: '2px' }}>
                                                    <div className="filtro-link d-flex align-items-center"
                                                        style={{ cursor: 'pointer', padding: '0.25rem 0.5rem' }}
                                                        onClick={() => filtrarPaciente(null)}>
                                                        <div className="icon text-center me-2">
                                                            <i
                                                                className="fa-solid fa-circle-check"
                                                                style={{
                                                                    borderRadius: "50%",
                                                                    color: "#47a0be",
                                                                    backgroundColor: todosPacientesSelecionados ? "white" : "#47a0be"
                                                                }}
                                                            ></i>
                                                        </div>
                                                        <span className="nav-link-text ms-1" style={{ fontSize: '13px' }}>
                                                            Selecionar Todos
                                                        </span>
                                                    </div>
                                                </li>
                                            
                                                {pacientesFiltroFiltrados && pacientesFiltroFiltrados.map((p) => (
                                                    <li className="nav-item" key={p.id} style={{ marginBottom: '2px' }}>
                                                        <div className="filtro-link d-flex align-items-center"
                                                            style={{ cursor: 'pointer', padding: '0.25rem 0.5rem' }}
                                                            onClick={() => filtrarPaciente(p)}>
                                                            <div className="icon text-center me-2">
                                                                <i className="fa-solid fa-circle-check"
                                                                    style={{
                                                                        borderRadius: "50%",
                                                                        color: "#47a0be",
                                                                        backgroundColor: pacientesDesselecionadosFiltro.includes(p.id) ? "#47a0be" : "white"
                                                                    }}>
                                                                </i>
                                                            </div>
                                                            <span className="nav-link-text ms-1" style={{ fontSize: '13px' }}>
                                                                {p.name}
                                                            </span>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <h6>Tipo</h6>
                                        <div className="card container" style={{ height: "10em", overflowY: "auto" }}>
                                            <ul className="navbar-nav" style={{ padding: '0', margin: '0' }}>
                                                <li className="nav-item" style={{ marginBottom: '2px' }}>
                                                    <div className="filtro-link d-flex align-items-center"
                                                        style={{ cursor: 'pointer', padding: '0.25rem 0.5rem' }}
                                                        onClick={() => filtrarTipo(null)}>
                                                        <div className="icon text-center me-2">
                                                            <i
                                                                className="fa-solid fa-circle-check"
                                                                style={{
                                                                    borderRadius: "50%",
                                                                    color: "#47a0be",
                                                                    backgroundColor: !todosTiposSelecionados ? "#47a0be" : "white",
                                                                    fontSize: '12px'
                                                                }}
                                                            ></i>
                                                        </div>
                                                        <span className="nav-link-text ms-1" style={{ fontSize: '13px' }}>
                                                            Selecionar Todos
                                                        </span>
                                                    </div>
                                                </li>
                                            </ul>
                                            <ul className="navbar-nav" style={{ padding: '0', margin: '0' }}>
                                                {tiposAgendamento && tiposAgendamento.map((t) => (
                                                    <li className="nav-item" key={t.id} style={{ marginBottom: '2px' }}>
                                                        <div className="filtro-link d-flex align-items-center"
                                                            style={{ cursor: 'pointer', padding: '0.25rem 0.5rem' }}
                                                            onClick={() => filtrarTipo(t)}>
                                                            <div className="icon text-center me-2">
                                                                <i className="fa-solid fa-circle-check"
                                                                    style={{
                                                                        borderRadius: "50%",
                                                                        color: "#47a0be",
                                                                        backgroundColor: tiposDesselecionadosFiltro.some(id => parseInt(id, 10) === parseInt(t.id, 10)) ? '#47a0be' : 'white',
                                                                        fontSize: '12px'
                                                                    }}>
                                                                </i>
                                                            </div>
                                                            <span className="nav-link-text ms-1" style={{ fontSize: '13px' }}>
                                                                {t.nome}
                                                            </span>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <h6>Ações</h6>
                                        <div className="d-flex flex-column gap-2" style={{ height: "10em", justifyContent: "center" }}>
                                            <Link
                                                to="/Calendario/Disponibilidade"
                                                className="btn btn-sm btn-success"
                                                title="Consultar Disponibilidade"
                                                style={{ fontSize: '11px', padding: '0.4rem 0.5rem' }}
                                            >
                                                <i className="bi bi-calendar2-check me-1"></i>
                                                Disponibilidade
                                            </Link>
                                            {!isReadOnly && (
                                                <>
                                                    <button
                                                        className="btn btn-sm btn-info"
                                                        onClick={() => setShowModalReplicarSem(true)}
                                                        title="Replicar Semana"
                                                        style={{ fontSize: '11px', padding: '0.4rem 0.5rem' }}
                                                    >
                                                        Replicar Semana
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-info"
                                                        onClick={() => setShowModalReplicarDia(true)}
                                                        title="Replicar Dia"
                                                        style={{ fontSize: '11px', padding: '0.4rem 0.5rem' }}
                                                    >
                                                        Replicar Dia
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body p-3" style={{ position: 'relative' }}>
                            {loading && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(255, 255, 255, 0.65)',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        zIndex: 10,
                                        borderRadius: 'inherit',
                                    }}
                                >
                                    <div className="d-flex flex-column align-items-center">
                                        <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                                            <span className="visually-hidden">Carregando...</span>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="spinner-grow text-primary me-2" role="status" style={{ width: '0.8rem', height: '0.8rem', animationDelay: '0s' }}></div>
                                            <div className="spinner-grow text-primary me-2" role="status" style={{ width: '0.8rem', height: '0.8rem', animationDelay: '0.2s' }}></div>
                                            <div className="spinner-grow text-primary" role="status" style={{ width: '0.8rem', height: '0.8rem', animationDelay: '0.4s' }}></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <style>
                                {`
                                /* Estilos para o popover "ver mais" */
                                .fc-popover {
                                    max-height: 300px !important;
                                    overflow-y: auto !important;
                                    z-index: 1000 !important;
                                    box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
                                    border-radius: 8px !important;
                                }
                                
                                .fc-popover-body {
                                    max-height: 250px !important;
                                    overflow-y: auto !important;
                                    padding: 8px !important;
                                }
                                
                                .fc-popover .fc-event {
                                    margin-bottom: 4px !important;
                                    border-radius: 4px !important;
                                }
                                
                                /* Garantir que modais fiquem sobre o popover */
                                .modal {
                                    z-index: 1050 !important;
                                }
                                
                                .modal-backdrop {
                                    z-index: 1040 !important;
                                }
                                `}
                            </style>
                            <FullCalendar
                                ref={calendarRef}
                                {...calendarConfig}
                                events={(fetchInfo, successCallback, failureCallback) => {
                                    
                                    // Aplicar os filtros aos eventos
                                    const eventosFiltrados = filtrarEventos(eventos);
                                    
                                    // Chamar o callback com os eventos filtrados
                                    successCallback(eventosFiltrados);
                                }}
                                datesSet={handleDatesSet}
                                dateClick={handleDateClick}
                                eventClick={handleEventClick}
                                eventDrop={handleEventDrop}
                            />
                        </div>
                    </div>
                </div>
            </div>



            {/* Modal de Novo Evento */}
            <Modal show={showModalAdd} onHide={() => setShowModalAdd(false)} centered>
                <div className="modal-content">
                    <div className="modal-header text-center" style={{ display: "block", paddingBottom: "0px" }}>
                        <h5 className="modal-title">Novo Evento</h5>
                        <button type="button" className="btn-close" onClick={() => setShowModalAdd(false)} aria-label="Close"></button>
                    </div>
                    <Form onSubmit={isReadOnly ? (e) => { e.preventDefault(); } : adicionarEvento}>
                        <div className="modal-body">
                            <div className="col-md-12 container">
                                <div className="row">
                                    <label>Título</label>
                                    <input
                                        type="text"
                                        id="titleAdd"
                                        name="titulo"
                                        className="form-control form-control-sm"
                                        value={titulo}
                                        onChange={(e) => setTitulo(e.target.value)}
                                        readOnly={tipoAgendAdd === 1}
                                    />

                                    <div className="row">
                                        <div className="col-md-3">
                                            <label>Data Início</label>
                                            <div className="input-group mb-3">
                                                <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                                                <DateInput
                                                    id="startAdd"
                                                    name="startDate"
                                                    className="form-control form-control-sm"
                                                    value={dataInicio}
                                                    onChange={handleDataInicioChange}
                                                    placeholder="dd/MM/yyyy"
                                                    inputMode="numeric"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <label>Hora Início</label>
                                            <div className="input-group mb-3">
                                                <span className="input-group-text"><i className="fa-solid fa-clock"></i></span>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={horaInicio}
                                                    onChange={handleHoraInicioChange}
                                                    disabled={allDay}
                                                >
                                                    {horarios
                                                        .filter(h => h >= "00:00" && h <= "23:00")
                                                        .map((hora) => (
                                                            <option key={hora} value={hora}>{hora}</option>
                                                        ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            {tipoAgendAdd !== 1 && (
                                                <div>
                                                    <label>&nbsp;</label>
                                                    <div className="form-check form-switch mt-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="allDay"
                                                            id="allDay"
                                                            checked={allDay}
                                                            onChange={handleAllDayChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="allDay">Dia Todo</label>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-3">
                                            <label>Tipo</label>
                                            <div className="input-group mb-3">
                                                <span className="input-group-text"><i className="fa-solid fa-circle-info"></i></span>
                                                <select className='form-control form-control-sm' onChange={alteraTipo} value={tipoAgendAdd}>
                                                    {tiposAgendamento.map(t => (
                                                        <option key={t.id} value={t.id}>
                                                            {t.nome}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-3">
                                            <label>Data Fim</label>
                                            <div className="input-group mb-3">
                                                <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                                                <DateInput
                                                    id="endAdd"
                                                    name="endDate"
                                                    className="form-control form-control-sm"
                                                    value={dataFim}
                                                    onChange={handleDataFimChange}
                                                    placeholder="dd/MM/yyyy"
                                                    inputMode="numeric"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <label>Hora Fim</label>
                                            <div className="input-group mb-3">
                                                <span className="input-group-text"><i className="fa-solid fa-clock"></i></span>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={horaFim}
                                                    onChange={handleHoraFimChange}
                                                    disabled={allDay}
                                                >
                                                    {horarios
                                                        .filter(h => h >= "01:00" && h <= "23:59")
                                                        .map((hora) => (
                                                            <option key={hora} value={hora}>{hora}</option>
                                                        ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            {tipoAgendAdd === 1 && (
                                                <>
                                                    <label>&nbsp;</label>
                                                    <div className="form-check form-switch mt-2">
                                                        <input className="form-check-input" type="checkbox" name="reposicao" id="reposicao" onChange={alterReposicao} checked={reposicao === 1} />
                                                        <label className="form-check-label" htmlFor="reposicao">Reposição</label>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <hr />

                                    {/* Pacientes - Tipo Sessão */}
                                    {tipoAgendAdd === 1 && (
                                        <div className="row" id="TipoSessao">
                                            <div className="col-md-6">
                                                <label>Paciente</label>
                                                <div className="input-group texto mb-3">
                                                    <span className="input-group-text">
                                                        <i className="fa-solid fa-child-reaching"></i>
                                                    </span>
                                                    <div className="flex-grow-1">
                                                        <CustomAutoComplete
                                                            placeholder="Digite o nome do paciente"
                                                            value={pacienteSearch}
                                                            setValue={setPacienteSearch}
                                                            onSearch={buscarPacientes}
                                                            onSelect={(item) => {
                                                                setFormAddEvent((prev) => ({
                                                                    ...prev,
                                                                    patientId: item.id,
                                                                }));
                                                                setPacienteSelecionado(item);

                                                                // foca diretamente no select
                                                                setTimeout(() => {
                                                                    if (planosRef.current) {
                                                                        planosRef.current.focus();
                                                                    }
                                                                }, 500);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-md-6">
                                                <div id="div-planos-add" style={{ display: pacienteSelecionado ? "block" : "none" }}>
                                                    <label>Plano</label>
                                                    {planosPaciente.length === 0 && pacienteSelecionado ? (
                                                        <div className="alert alert-warning py-2 mb-0" role="alert" style={{ marginTop: '0', fontSize: '0.875rem' }}>
                                                            <small>
                                                                <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                                                Paciente sem plano selecionado
                                                            </small>
                                                        </div>
                                                    ) : (
                                                        <select
                                                            ref={planosRef}
                                                            name="planId"
                                                            id="planoAdd"
                                                            className="form-control form-control-sm"
                                                            onChange={handleInputChange}
                                                            onBlur={SetarTitulo}
                                                            onFocus={SetarTitulo}
                                                            value={formAddEvent.planId || ''}
                                                        >
                                                            {planosPaciente.map((plano) => (
                                                                <option key={plano.plan.id} value={plano.plan.id}>
                                                                    {plano.plan.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Tipo Avulso */}
                                    {tipoAgendAdd === 2 && (
                                        <div className="row" id="TipoReavaliacao">
                                            <div className="col-md-6">
                                                <label>Paciente</label>
                                                <div className="flex-grow-1">
                                                    <CustomAutoComplete
                                                        placeholder="Digite o nome do paciente"
                                                        value={pacienteSearch}
                                                        setValue={setPacienteSearch}
                                                        onSearch={buscarPacientes}
                                                        onSelect={(item) => {
                                                            setFormAddEvent((prev) => ({
                                                                ...prev,
                                                                patientId: item.id,
                                                            }));
                                                            setPacienteSelecionado(item);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <label>Valor</label>
                                                <input type="text" className="form-control form-control-sm" id="valor" name='value' onChange={handleInputChange} value={formAddEvent.value} />
                                            </div>
                                            <div className="col-md-3">
                                                <label>Considerar Valor?</label>
                                                <div className="form-check form-switch">
                                                    <input className="form-check-input" type="checkbox" name="consideraValor" id="consideraValorAdd" onChange={alterConsideraValor} checked={consideraValor === 1} />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Terapeuta Principal */}
                                    {(tipoAgendAdd === 2 || tipoAgendAdd === 1) && (
                                        <>
                                            <div className="col-md-6">
                                                <label>Terapeuta</label>
                                                <div className="input-group texto mb-3">
                                                    <span className="input-group-text"><i className="fa-solid fa-people-group"></i></span>
                                                    <div className="flex-grow-1">
                                                        <CustomAutoComplete
                                                            placeholder="Digite o nome do terapeuta"
                                                            value={terapeutaSearch}
                                                            setValue={setTerapeutaSearch}
                                                            onSearch={buscarTerapeutas}
                                                            onSelect={(item) => {
                                                                setFormAddEvent((prev) => ({ ...prev, therapistId: item.id }));
                                                                setTerapeutaSelecionado(item);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Funções da Terapeuta */}
                                            {terapeutaSelecionado && (
                                                <div className="col-md-6">
                                                    <label>Função</label>
                                                    {funcoesTerapeuta.length === 0 ? (
                                                        <div className="alert alert-warning py-2 mb-0" role="alert" style={{ marginTop: '0', fontSize: '0.875rem' }}>
                                                            <small>
                                                                <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                                                Terapeuta sem função definida
                                                            </small>
                                                        </div>
                                                    ) : (
                                                        <select
                                                            name="jobPositionId"
                                                            className="form-control form-control-sm"
                                                            value={formAddEvent.jobPositionId || ''}
                                                            onChange={(e) => setFormAddEvent((prev) => ({ ...prev, jobPositionId: parseInt(e.target.value) }))}
                                                        >
                                                            {funcoesTerapeuta.map((func) => (
                                                                <option key={func.id} value={func.jobPositionId}>
                                                                    {func?.jobPosition?.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* Outras Terapeutas */}
                                    {terapeutaSelecionado && terapeutaSearch.trim().length > 0 && (
                                        <>
                                            <div className="col-md-12 mb-2">
                                                <label>Outras Terapeutas</label>
                                                <CustomAutoComplete
                                                    placeholder="Digite o nome de outra terapeuta"
                                                    value={outraTerapSearch}
                                                    setValue={setOutraTerapSearch}
                                                    onSearch={buscarTerapeutasExtras}
                                                    onSelect={handleSelecionarOutraTerapeuta}
                                                />
                                            </div>

                                            {/* Lista de terapeutas adicionadas */}
                                            {showModalAdd && outrasTerapeutas.map((t, index) => (
                                                <div className="col-md-12 row mb-2" key={t.id}>
                                                    <div className="col-md-6">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={t.name}
                                                            readOnly
                                                        />
                                                    </div>
                                                    <div className="col-md-5">
                                                        <select
                                                            className="form-control"
                                                            value={t.selectedFuncaoId}
                                                            onChange={(e) => {
                                                                const novaFuncaoId = parseInt(e.target.value);

                                                                // atualiza o estado de `outrasTerapeutas`
                                                                setOutrasTerapeutas((prev) => {
                                                                    const novo = [...prev];
                                                                    novo[index].selectedFuncaoId = novaFuncaoId;
                                                                    return novo;
                                                                });

                                                                // atualiza o vetor `relatedAppointments`
                                                                setAgendamentosRelacionados((prev) => {
                                                                    const novo = [...prev];
                                                                    const pos = novo.findIndex((r) => r.therapistId === t.id);
                                                                    if (pos !== -1) {
                                                                        novo[pos].jobPositionId = novaFuncaoId;
                                                                    }
                                                                    return novo;
                                                                });
                                                            }}
                                                        >
                                                            {t.funcoes.map((f) => (
                                                                <option key={f.id} value={f.id}>
                                                                    {f.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="col-md-1 d-flex align-items-center justify-content-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => {
                                                                setOutrasTerapeutas((prev) => prev.filter((_, i) => i !== index));
                                                                setIdsTerapeutasExtras((prev) => prev.filter((id) => id !== t.id));
                                                                setAgendamentosRelacionados((prev) => prev.filter((r) => r.therapistId !== t.id));
                                                            }}
                                                            style={{
                                                                paddingLeft: "0.8rem",
                                                                paddingRight: "0.8rem",
                                                                marginTop: "4px"
                                                            }}
                                                        >
                                                            <i className="fa-solid fa-xmark"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    )}

                                    <div className="row">
                                        <div className="col-md-12">
                                            <label>Descrição</label>
                                            <textarea
                                                id="observacao"
                                                name="note"
                                                className="form-control form-control-sm"
                                                onChange={(e) => setFormAddEvent((prev) => ({ ...prev, note: e.target.value }))}
                                                value={formAddEvent.note}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-info" onClick={() => setShowModalAdd(false)}>Fechar</button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={!podeAgendar || isReadOnly}
                                title={!podeAgendar ?
                                    (tipoAgendAdd === 1 && pacienteSelecionado && (!formAddEvent.planId || planosPaciente.length === 0) ?
                                        "Paciente precisa ter plano selecionado para agendar sessão" :
                                        (terapeutaSelecionado && (!formAddEvent.jobPositionId || funcoesTerapeuta.length === 0) ?
                                            "Terapeuta precisa ter função definida para agendar" :
                                            "Verifique os campos obrigatórios"
                                        )
                                    ) : ""
                                }
                            >
                                Agendar
                            </button>
                        </div>
                    </Form>
                </div>
            </Modal>

            {/* Modal de Edição de Evento */}
            <Modal show={showModalEdit} onHide={() => setShowModalEdit(false)} centered>
                <div className="modal-content">
                    <div className="modal-header text-center" style={{ display: "block", paddingBottom: "0px" }}>
                        <h5 className="modal-title">{isReadOnly ? "Visualizar Evento" : "Editar Evento"}</h5>
                        <button type="button" className="btn-close" onClick={() => setShowModalEdit(false)} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="col-md-12 container">
                            <form onSubmit={isReadOnly ? (e) => { e.preventDefault(); } : editarEvento}>
                                <div className="row">
                                    <label>Título</label>
                                    <input
                                        type="text"
                                        id="titleEdit"
                                        name="title"
                                        className="form-control form-control-sm"
                                        value={titulo}
                                        onChange={(e) => setTitulo(e.target.value)}
                                        readOnly={tipoAgendEdit === 1 || isReadOnly}
                                        disabled={isReadOnly}
                                    />

                                    <div className="row">
                                        <div className="col-md-3">
                                            <label>Data Início</label>
                                            <div className="input-group mb-3">
                                                <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                                                <DateInput
                                                    id="startEdit"
                                                    name="startDate"
                                                    className="form-control form-control-sm"
                                                    value={dataInicio}
                                                    onChange={handleDataInicioChange}
                                                    placeholder="dd/MM/yyyy"
                                                    inputMode="numeric"
                                                    disabled={isReadOnly}
                                                    readOnly={isReadOnly}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <label>Hora Início</label>
                                            <div className="input-group mb-3">
                                                <span className="input-group-text"><i className="fa-solid fa-clock"></i></span>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={horaInicio}
                                                    onChange={handleHoraInicioChange}
                                                    disabled={allDay}
                                                >
                                                    {horarios
                                                        .filter(h => h >= "00:00" && h <= "23:00")
                                                        .map((hora) => (
                                                            <option key={hora} value={hora}>{hora}</option>
                                                        ))}
                                                </select>
                                            </div>
                                        </div>
                                        {tipoAgendEdit !== 1 && (
                                            <div className="col-md-4">
                                                <div>
                                                    <label>&nbsp;</label>
                                                    <div className="form-check form-switch mt-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="allDay"
                                                            id="allDayEdit"
                                                            checked={allDay}
                                                            onChange={handleAllDayChange}
                                                            disabled={isReadOnly}
                                                        />
                                                        <label className="form-check-label" htmlFor="allDayEdit">Dia Todo</label>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {tipoAgendEdit === 1 && (
                                            <div className="col-md-4">
                                                <label>&nbsp;</label>
                                                <div className="form-check form-switch mt-2">
                                                    <input className="form-check-input" type="checkbox" name="reposicao" id="reposicaoEdit" onChange={alterReposicao} checked={reposicao === 1} disabled={isReadOnly} />
                                                    <label className="form-check-label" htmlFor="reposicaoEdit">Reposição</label>
                                                </div>
                                            </div>
                                        )}
                                        <div className="col-md-3">
                                            <label>Tipo</label>
                                            <div className="input-group mb-3">
                                                <span className="input-group-text"><i className="fa-solid fa-circle-info"></i></span>
                                                <select 
                                                    className='form-control form-control-sm' 
                                                    value={tipoAgendEdit} 
                                                    onChange={alteraTipoEdit}
                                                    disabled={isReadOnly}
                                                >
                                                    {tiposAgendamento.map(t => (
                                                        <option key={t.id} value={t.id}>
                                                            {t.nome}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-3">
                                            <label>Data Fim</label>
                                            <div className="input-group mb-3">
                                                <span className="input-group-text"><i className="fa-solid fa-calendar-days"></i></span>
                                                <DateInput
                                                    id="endEdit"
                                                    name="endDate"
                                                    className="form-control form-control-sm"
                                                    value={dataFim}
                                                    onChange={handleDataFimChange}
                                                    placeholder="dd/MM/yyyy"
                                                    inputMode="numeric"
                                                    disabled={isReadOnly}
                                                    readOnly={isReadOnly}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <label>Hora Fim</label>
                                            <div className="input-group mb-3">
                                                <span className="input-group-text"><i className="fa-solid fa-clock"></i></span>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={horaFim}
                                                    onChange={handleHoraFimChange}
                                                    disabled={allDay}
                                                >
                                                    {horarios
                                                        .filter(h => h >= "01:00" && h <= "23:59")
                                                        .map((hora) => (
                                                            <option key={hora} value={hora}>{hora}</option>
                                                        ))}
                                                </select>
                                            </div>
                                        </div>
                                        {tipoAgendEdit === 1 ? (
                                            <>
                                                <div className="col-md-2">
                                                    <label>&nbsp;</label>
                                                    <div className="form-check form-switch mt-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="falta"
                                                            id="faltaEdit"
                                                            onChange={alterFalta}
                                                            checked={falta === 1}
                                                            disabled={disabledFalta || isReadOnly}
                                                        />
                                                        <label className="form-check-label" htmlFor="faltaEdit">Falta</label>
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <label>&nbsp;</label>
                                                    <div className="form-check form-switch mt-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="faturada"
                                                            id="faturadaEdit"
                                                            onChange={alterFaturada}
                                                            checked={faturada === 1}
                                                            disabled={disabledFaturada || isReadOnly}
                                                        />
                                                        <label className="form-check-label" htmlFor="faturadaEdit">Faturada</label>
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <label>&nbsp;</label>
                                                    <div className="form-check form-switch mt-2">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="geraRepo"
                                                            id="geraRepoEdit"
                                                            onChange={alterGeraReposicao}
                                                            checked={geraReposicao === 1}
                                                            disabled={disabledGeraRepo || isReadOnly}
                                                        />
                                                        <label className="form-check-label" htmlFor="geraRepoEdit">Gera Reposição</label>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className='col-md-7'></div>
                                        )}
                                    </div>

                                    <hr />

                                    {/* Pacientes - Tipo Sessão */}
                                    {tipoAgendEdit === 1 && (
                                        <div className="row" id="TipoSessaoEdit">
                                            <div className="col-md-6">
                                                <label>Paciente</label>
                                                <div className="input-group texto mb-3">
                                                    <span className="input-group-text">
                                                        <i className="fa-solid fa-child-reaching"></i>
                                                    </span>
                                                    <div className="flex-grow-1">
                                                        <CustomAutoComplete
                                                            placeholder="Digite o nome do paciente"
                                                            value={pacienteSearch}
                                                            setValue={(value) => {
                                                                if (!isReadOnly) {
                                                                    setPacienteSearch(value);
                                                                    // Resetar a flag quando o usuário começar a digitar
                                                                    if (pacienteFoiSelecionadoExternamente) {
                                                                        setPacienteFoiSelecionadoExternamente(false);
                                                                        setPacienteSelecionado(null);
                                                                        setFormEditEvent((prev) => ({ ...prev, patientId: null }));
                                                                        setPlanosPaciente([]);
                                                                    }
                                                                }
                                                            }}
                                                            onSearch={buscarPacientes}
                                                            onSelect={(item) => {
                                                                if (!isReadOnly) {
                                                                    setFormEditEvent((prev) => ({
                                                                        ...prev,
                                                                        patientId: item.id,
                                                                    }));
                                                                    setPacienteSelecionado(item);
                                                                    setPacienteFoiSelecionadoExternamente(true);
                                                                    // Buscar planos do paciente
                                                                    buscarPlanosEdicao(item.id);
                                                                }
                                                            }}
                                                            externalUserSelected={pacienteFoiSelecionadoExternamente}
                                                            disabled={isReadOnly}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-md-6">
                                                <div id="div-planos-edit" style={{ display: pacienteSelecionado ? "block" : "none" }}>
                                                    <label>Plano</label>
                                                    <select
                                                        name="planId"
                                                        id="planoEdit"
                                                        className="form-control form-control-sm"
                                                        onChange={handleInputChangeEdit}
                                                        value={formEditEvent.planId || ''}
                                                        disabled={isReadOnly}
                                                    >
                                                        {planosPaciente.map((plano) => (
                                                            <option key={plano.plan.id} value={plano.plan.id}>
                                                                {plano.plan.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Tipo Avulso */}
                                    {tipoAgendEdit === 2 && (
                                        <div className="row" id="TipoReavaliacaoEdit">
                                            <div className="col-md-6">
                                                <label>Paciente</label>
                                                <div className="flex-grow-1">
                                                    <CustomAutoComplete
                                                        placeholder="Digite o nome do paciente"
                                                        value={pacienteSearch}
                                                        setValue={(value) => {
                                                            if (!isReadOnly) {
                                                                setPacienteSearch(value);
                                                                // Resetar a flag quando o usuário começar a digitar
                                                                if (pacienteFoiSelecionadoExternamente) {
                                                                    setPacienteFoiSelecionadoExternamente(false);
                                                                    setPacienteSelecionado(null);
                                                                    setFormEditEvent((prev) => ({ ...prev, patientId: null }));
                                                                }
                                                            }
                                                        }}
                                                        onSearch={buscarPacientes}
                                                        onSelect={(item) => {
                                                            if (!isReadOnly) {
                                                                setFormEditEvent((prev) => ({
                                                                    ...prev,
                                                                    patientId: item.id,
                                                                }));
                                                                setPacienteSelecionado(item);
                                                                setPacienteFoiSelecionadoExternamente(true);
                                                            }
                                                        }}
                                                        externalUserSelected={pacienteFoiSelecionadoExternamente}
                                                        disabled={isReadOnly}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <label>Valor</label>
                                                <input type="text" className="form-control form-control-sm" id="valorEdit" name='value' onChange={handleInputChangeEdit} value={formEditEvent.value} disabled={isReadOnly} readOnly={isReadOnly} />
                                            </div>
                                            <div className="col-md-3">
                                                <label>Considerar Valor?</label>
                                                <div className="form-check form-switch">
                                                    <input className="form-check-input" type="checkbox" name="consideraValor" id="consideraValorEdit" onChange={alterConsideraValor} checked={consideraValor === 1} disabled={isReadOnly} />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Terapeuta Principal */}
                                    {tipoAgendEdit !== 0 && (
                                        <div className="col-md-6">
                                            <label>Terapeuta</label>
                                            <div className="input-group texto mb-3">
                                                <span className="input-group-text"><i className="fa-solid fa-people-group"></i></span>
                                                <div className="flex-grow-1">
                                                    <CustomAutoComplete
                                                        placeholder="Digite o nome do terapeuta"
                                                        value={terapeutaSearch}
                                                        setValue={(value) => {
                                                            if (!isReadOnly) {
                                                                setTerapeutaSearch(value);
                                                                // Resetar a flag quando o usuário começar a digitar
                                                                if (terapeutaFoiSelecionadoExternamente) {
                                                                    setTerapeutaFoiSelecionadoExternamente(false);
                                                                    setTerapeutaSelecionado(null);
                                                                    setFormEditEvent((prev) => ({ ...prev, therapistId: null }));
                                                                }
                                                            }
                                                        }}
                                                        onSearch={buscarTerapeutas}
                                                        onSelect={(item) => {
                                                            if (!isReadOnly) {
                                                                setFormEditEvent((prev) => ({ ...prev, therapistId: item.id }));
                                                                setTerapeutaSelecionado(item);
                                                                setTerapeutaFoiSelecionadoExternamente(true);
                                                            }
                                                        }}
                                                        externalUserSelected={terapeutaFoiSelecionadoExternamente}
                                                        disabled={isReadOnly}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Funções da Terapeuta */}
                                    {tipoAgendEdit !== 0 && (
                                        <>
                                            {terapeutaSelecionado && funcoesTerapeuta.length > 0 && (
                                                <div className="col-md-6">
                                                    <label>Função</label>
                                                    <select
                                                        name="funcaoId"
                                                        className="form-control form-control-sm"
                                                        value={formEditEvent.jobPositionId || ''}
                                                        onChange={(e) => setFormEditEvent((prev) => ({ ...prev, jobPositionId: parseInt(e.target.value) }))}
                                                        disabled={isReadOnly}
                                                    >
                                                        {funcoesTerapeuta.map((func) => (
                                                            <option key={func.id} value={func.jobPositionId}>
                                                                {func.jobPosition?.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* Outras Terapeutas */}
                                    {tipoAgendEdit !== 0 && (
                                        <>
                                            {terapeutaSelecionado && terapeutaSearch.trim().length > 0 && (
                                                <>
                                                    <div className="col-md-12 mb-2">
                                                        <label>Outras Terapeutas</label>
                                                        <CustomAutoComplete
                                                            placeholder="Digite o nome de outra terapeuta"
                                                            value={outraTerapSearch}
                                                            setValue={setOutraTerapSearch}
                                                            onSearch={buscarTerapeutasExtras}
                                                            onSelect={handleSelecionarOutraTerapeuta}
                                                            disabled={isReadOnly}
                                                        />
                                                    </div>

                                                    {/* Lista de terapeutas adicionadas */}
                                                    {showModalEdit && outrasTerapeutas.map((t, index) => (
                                                        <div className="col-md-12 row mb-2" key={t.therapistId || t.id}>
                                                            {t.isNova ? (
                                                                <>
                                                                    <div className="col-md-6">
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            value={t.name}
                                                                            readOnly
                                                                        />
                                                                    </div>
                                                                    <div className="col-md-5">
                                                                        <select
                                                                            className="form-control"
                                                                            value={t.selectedFuncaoId}
                                                                            onChange={(e) => {
                                                                                if (!isReadOnly) {
                                                                                    const novaFuncaoId = parseInt(e.target.value);

                                                                                    setOutrasTerapeutas((prev) => {
                                                                                        const novo = [...prev];
                                                                                        novo[index].selectedFuncaoId = novaFuncaoId;
                                                                                        return novo;
                                                                                    });

                                                                                    setAgendamentosRelacionados((prev) => {
                                                                                        const novo = [...prev];
                                                                                        const pos = novo.findIndex((r) => r.therapistId === t.id);
                                                                                        if (pos !== -1) {
                                                                                            novo[pos].jobPositionId = novaFuncaoId;
                                                                                        }
                                                                                        return novo;
                                                                                    });
                                                                                }
                                                                            }}
                                                                            disabled={isReadOnly}
                                                                        >
                                                                            {t.funcoes.map((f) => (
                                                                                <option key={f.id} value={f.id}>
                                                                                    {f.name}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className="col-md-6">
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            value={t.therapistName}
                                                                            readOnly
                                                                        />
                                                                    </div>
                                                                    <div className="col-md-5">
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            value={t.jobPositionName}
                                                                            readOnly
                                                                        />
                                                                    </div>
                                                                </>
                                                            )}
                                                            <div className="col-md-1 d-flex align-items-center justify-content-center">
                                                                {!isReadOnly && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-danger"
                                                                        onClick={async () => {
                                                                            try {
                                                                                const response = await authFetch(`appointment/${t.appointmentId}`, {
                                                                                    method: "DELETE"
                                                                                });

                                                                                if (!response.ok) throw new Error("Erro ao excluir terapeuta extra");

                                                                                // Atualiza os estados somente após o DELETE
                                                                                setOutrasTerapeutas((prev) => prev.filter((_, i) => i !== index));
                                                                                setIdsTerapeutasExtras((prev) => prev.filter((id) => id !== t.therapistId));
                                                                                setAgendamentosRelacionados((prev) => prev.filter((r) => r.therapistId !== t.therapistId));

                                                                                showNotification("success", "Terapeuta extra removida com sucesso.");
                                                                            } catch (err) {
                                                                                console.error(err);
                                                                                showNotification("danger", "Erro ao remover terapeuta extra.");
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            paddingLeft: "0.8rem",
                                                                            paddingRight: "0.8rem",
                                                                            marginTop: "4px"
                                                                        }}
                                                                    >
                                                                        <i className="fa-solid fa-xmark"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </>
                                            )}
                                        </>
                                    )}

                                    <div className="col-md-12">
                                        <label>Descrição</label>
                                        <textarea
                                            id="observacaoEdit"
                                            name="note"
                                            className="form-control form-control-sm"
                                            onChange={(e) => setFormEditEvent((prev) => ({ ...prev, note: e.target.value }))}
                                            value={formEditEvent.note}
                                            disabled={isReadOnly}
                                            readOnly={isReadOnly}
                                        />
                                    </div>
                                </div>

                                {falta === 1 && infoFalta && (infoFalta.note || infoFalta.markedByName || infoFalta.markedAt) && (
                                    <div className="card bg-light border-0 mt-3" style={{ padding: '0.75rem 1rem' }}>
                                        <div className="text-uppercase text-secondary font-weight-bold mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.02em' }}>
                                            Observação do responsável
                                        </div>
                                        <p className="mb-2" style={{ fontSize: '0.95rem', color: '#344767', whiteSpace: 'pre-wrap' }}>
                                            {infoFalta.note || <span className="text-secondary fst-italic">Sem observação</span>}
                                        </p>
                                        {(infoFalta.markedByName || infoFalta.markedAt) && (
                                            <div className="text-secondary" style={{ fontSize: '0.72rem' }}>
                                                {infoFalta.markedByName}
                                                {infoFalta.markedByName && infoFalta.markedAt && ' • '}
                                                {infoFalta.markedAt && new Date(infoFalta.markedAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '')}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {infoCriacao && (infoCriacao.createdAt || infoCriacao.createdByName) && (
                                    <div className="text-secondary mt-3" style={{ fontSize: '0.72rem' }}>
                                        Criado
                                        {infoCriacao.createdByName && <> por {infoCriacao.createdByName}</>}
                                        {infoCriacao.createdAt && <> em {new Date(infoCriacao.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '')}</>}
                                    </div>
                                )}

                                <div className="modal-footer row container" style={{ borderTop: 0 }}>
                                    <div className="row">
                                        {!isReadOnly && (userAPIData.userRoleId === 1 || userAPIData.userRoleId === 4) && (
                                            <div className="col-md-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-danger"
                                                    onClick={() => {
                                                        setEventoParaExcluir(idEventoSelecionado);
                                                        setShowModalConfirmacao(true);
                                                    }}
                                                >
                                                    Excluir
                                                </button>
                                            </div>
                                        )}
                                        <div className={isReadOnly ? "col-md-10" : (userAPIData.userRoleId <= 2 ? "col-md-6" : "col-md-8")}></div>
                                        <div className="col-md-2">
                                            <button type="button" className="btn btn-info" onClick={() => setShowModalEdit(false)}>Fechar</button>
                                        </div>
                                        {!isReadOnly && (
                                            <div className="col-md-2">
                                                <button type="submit" className="btn btn-primary">Atualizar</button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Modal de Confirmação de Exclusão */}
            <Modal show={showModalConfirmacao} onHide={() => setShowModalConfirmacao(false)} centered>
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Confirmar Exclusão</h5>
                        <button type="button" className="btn-close" onClick={() => setShowModalConfirmacao(false)} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <p>Tem certeza que deseja excluir este evento?</p>
                        <p className="text-muted">Esta ação não pode ser desfeita.</p>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setShowModalConfirmacao(false)}>
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-danger" onClick={confirmarExclusaoEvento}>
                            Sim, Excluir
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Modal Replicar Dia */}
            <Modal show={showModalReplicarDia} onHide={() => setShowModalReplicarDia(false)} centered>
                <div className="modal-content">
                    <Modal.Header className="modal-header text-center" style={{ display: "block", paddingBottom: "0px" }}>
                        <h5 className="modal-title">Copiar Dia</h5>
                        <button type="button" className="btn-close" onClick={() => setShowModalReplicarDia(false)} aria-label="Close"></button>
                    </Modal.Header>

                    <Modal.Body className="modal-body">
                        <form onSubmit={handleReplicarDia}>
                            <div className="col-md-12 container">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="dataInicio" className="form-label">Data Início</label>
                                        <DateInput
                                            id="dataInicio"
                                            className="form-control form-control-sm"
                                            value={dataInicioRepli}
                                            onChange={(e) => setDataInicioRepli(e.target.value)}
                                            placeholder="dd/MM/yyyy"
                                            inputMode="numeric"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="dataFim" className="form-label">Data Fim</label>
                                        <DateInput
                                            id="dataFim"
                                            className="form-control form-control-sm"
                                            value={dataFimRepli}
                                            onChange={(e) => setDataFimRepli(e.target.value)}
                                            placeholder="dd/MM/yyyy"
                                            inputMode="numeric"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <Modal.Footer className="modal-footer row container">
                                <div className="row w-100">
                                    <div className="col-md-6">
                                        <button type="button" className="btn btn-info w-100" onClick={() => setShowModalReplicarDia(false)}>Fechar</button>
                                    </div>
                                    <div className="col-md-6">
                                        <button type="submit" className="btn btn-primary w-100">Replicar</button>
                                    </div>
                                </div>
                            </Modal.Footer>
                        </form>
                    </Modal.Body>
                </div>
            </Modal>

            {/* Modal Replicar Semana */}
            <Modal show={showModalReplicarSem} onHide={() => setShowModalReplicarSem(false)} centered>
                <div className="modal-content">
                    <Modal.Header className="modal-header text-center" style={{ display: "block", paddingBottom: "0px" }}>
                        <h5 className="modal-title">Replicar Semana</h5>
                        <button type="button" className="btn-close" onClick={() => setShowModalReplicarSem(false)} aria-label="Close"></button>
                    </Modal.Header>

                    <Modal.Body className="modal-body">
                        <form onSubmit={handleReplicarSemana}>
                            <div className="col-md-12 container">
                                <div className="col-md-12 row">
                                    <label>Selecione a Semana Origem</label>
                                    <input
                                        type="week"
                                        name="semOrigem"
                                        className="form-control form-control-sm mb-3"
                                        value={semOrigem}
                                        onChange={(e) => setSemOrigem(e.target.value)}
                                        required
                                    />
                                    <label>Selecione a Semana Destino</label>
                                    <input
                                        type="week"
                                        name="semDestino"
                                        className="form-control form-control-sm"
                                        value={semDestino}
                                        onChange={(e) => setSemDestino(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <Modal.Footer className="modal-footer container">
                                <div className="col-md-12 text-center">
                                    <button type="button" className="btn btn-sm btn-danger me-2" onClick={() => setShowModalReplicarSem(false)}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn btn-sm btn-primary">
                                        Replicar
                                    </button>
                                </div>
                            </Modal.Footer>
                        </form>
                    </Modal.Body>
                </div>
            </Modal>


        </div>
    );
});

export default Calendario; 